import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, Check, Edit3, History, Lock, RotateCcw, Unlock, X } from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import ServiceLifecycleDialog from "../components/service/ServiceLifecycleDialog";
import ServiceStageHistoryDialog, {
  normalizeServiceStageHistory,
} from "../components/service/ServiceStageHistoryDialog";
import VoidWarrantySwitch from "../components/service/VoidWarrantySwitch";
import ProtectedResolutionField from "../components/service/ProtectedResolutionField";
import CannibalPartSelector from "../components/service/CannibalPartSelector";
import UnifiedUsedPartPanel from "../components/service/UnifiedUsedPartPanel";
import CannibalStockSourceSnapshot from "../components/service/CannibalStockSourceSnapshot";
import ServiceCostSection from "../components/service/ServiceCostSection";
import ServiceCostReview from "../components/service/ServiceCostReview";
import { FlowpiAlertDialog, FlowpiDialogSurface } from "../components/ui/flowpi-dialog";
import { FormError } from "../components/ui/feedback";
import { formatApiError, getServiceErrorPayload } from "../lib/api";
import {
  checkServiceSerialNumber,
  getServiceSparePartOptions,
  getServiceTicket,
  getServiceTicketStageHistory,
  listActiveServiceDeviceModels,
  listActiveServiceTechnicians,
  normalizeServiceSelectorResponse,
  normalizeServiceTicketResponse,
  restoreServiceTicket,
  updateServiceTicket,
} from "../lib/serviceApi";
import {
  deviceModelOptionsWithHistory,
  modelNameForSelection,
} from "../lib/serviceFormOptions";
import {
  SERVICE_BRANCH_LABELS,
  SERVICE_CATEGORY_LABELS,
  SERVICE_DVT_STATUS_LABELS,
  SERVICE_STAGE_LABELS,
  SERVICE_STAGES,
} from "../lib/serviceConstants";
import { canMutateServiceCost, canRestoreServiceTicket, getServiceEditCapabilities } from "../lib/servicePermissions";
import {
  deriveVoidWarrantyControlState,
  serviceOperationalDateFromTimestamp,
} from "../lib/serviceWarranty";
import { Button } from "../components/ui/button";
import {
  PersistentHeaderUtilityAction,
  PersistentPageHeader,
  PersistentPageLoading,
} from "../components/ui/page-structure";
import UnsavedChangesDialog from "../components/ui/unsaved-changes-dialog";
import {
  ABSENT_VALUE,
  DataValue,
  PassiveBadge,
  displayValue,
  formatPresentationTimestamp,
} from "../components/ui/data-presentation";
import { ServiceDvtBadge } from "../components/service/ServiceListControls";
import {
  buildServiceCostMutation,
  deriveServiceCostDraftPreview,
  projectSystemManagedDiscount,
  serviceCostDraft,
  validateServiceCostDraft,
} from "../lib/serviceCost";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const EMPTY = ABSENT_VALUE;
const LOGISTICS_EDIT_FIELD_PATHS = new Set(["expedition", "tracking_number", "notes"]);

const BRANCH_OPTIONS = [
  { value: "jakarta", label: "Jakarta" },
  { value: "surabaya", label: "Surabaya" },
  { value: "bali", label: "Bali" },
];

const CATEGORY_OPTIONS = [
  { value: "sold", label: "Sold" },
  { value: "rental", label: "Rental" },
  { value: "stock", label: "Stock" },
];

const ACCESSORY_OPTIONS = [
  { value: "imin_box", label: "iMin Box" },
  { value: "adapter_charger", label: "Adapter / Charger" },
  { value: "printer", label: "Printer" },
  { value: "scanner", label: "Scanner" },
  { value: "docking", label: "Docking" },
  { value: "second_screen", label: "Second Screen" },
  { value: "second_screen_cable", label: "Second Screen Cable" },
  { value: "manual_book_warranty_card", label: "Manual Book / Warranty Card" },
  { value: "other", label: "Other" },
];

const ACCESSORY_LABELS = Object.fromEntries(ACCESSORY_OPTIONS.map((option) => [option.value, option.label]));

const IMIN_STATUS_OPTIONS = [
  { value: "unknown", label: "Unknown" },
  { value: "valid", label: "Valid" },
  { value: "invalid", label: "Invalid" },
];

const IMIN_STATUS_LABELS = Object.fromEntries(IMIN_STATUS_OPTIONS.map((option) => [option.value, option.label]));

const REPAIR_ACTION_OPTIONS = [
  { value: "part_replace", label: "Part Replace" },
  { value: "part_repair", label: "Part Repair" },
  { value: "software_repair", label: "Software Repair" },
  { value: "cancel_service", label: "Cancel Service" },
];

const REPAIR_ACTION_LABELS = Object.fromEntries(REPAIR_ACTION_OPTIONS.map((option) => [option.value, option.label]));
export const SERVICE_EDIT_STAGE_OPTIONS = Object.values(SERVICE_STAGES).map((value) => ({
  value,
  label: SERVICE_STAGE_LABELS[value],
}));
const CURRENT_YEAR = new Date().getFullYear();
const OPERATIONAL_YEAR_OPTIONS = Array.from({ length: CURRENT_YEAR - 2020 + 1 }, (_item, index) => String(2020 + index));
const EXPIRY_YEAR_OPTIONS = Array.from({ length: CURRENT_YEAR + 3 - 2020 + 1 }, (_item, index) => String(2020 + index));
const DAY_OPTIONS = Array.from({ length: 31 }, (_item, index) => String(index + 1).padStart(2, "0"));
const MONTH_OPTIONS = [
  { value: "01", label: "Jan" },
  { value: "02", label: "Feb" },
  { value: "03", label: "Mar" },
  { value: "04", label: "Apr" },
  { value: "05", label: "May" },
  { value: "06", label: "Jun" },
  { value: "07", label: "Jul" },
  { value: "08", label: "Aug" },
  { value: "09", label: "Sep" },
  { value: "10", label: "Oct" },
  { value: "11", label: "Nov" },
  { value: "12", label: "Dec" },
];

function unwrapTechnicians(response) {
  const data = response?.data || response || {};
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.data)) return data.data;
  return [];
}

function display(value) {
  if (value === null || value === undefined || value === "") return EMPTY;
  return value;
}

function formatDateTime(value) {
  return formatPresentationTimestamp(value);
}

function parseISODateOnly(value) {
  if (!value || typeof value !== "string") return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return date;
}

function formatISODateOnly(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function displayDateOnly(value) {
  if (!value) return "";
  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return "";
  return formatISODateOnly(date);
}

function splitDateValue(value) {
  const text = displayDateOnly(value) || normalizeString(value);
  const parts = text.split("-");
  return {
    year: parts[0] || "",
    month: parts[1] || "",
    day: parts[2] || "",
  };
}

function formatDate(value) {
  if (!value) return EMPTY;
  return String(value);
}

function formatLabel(labels, value) {
  return value ? labels[value] || value : EMPTY;
}

function dateInDateOnly(ticket) {
  if (!ticket?.date_in) return new Date();
  return serviceOperationalDateFromTimestamp(ticket.date_in) || new Date();
}

function parseStrictPositiveInteger(value) {
  const text = normalizeString(value).trim();
  if (!/^[1-9]\d*$/.test(text)) return null;
  const parsed = Number(text);
  if (!Number.isSafeInteger(parsed) || parsed < 1) return null;
  return parsed;
}

export function calculateDvtPreview(purchaseDateValue, durationValue, ticket) {
  const purchaseDate = parseISODateOnly(purchaseDateValue);
  const durationYears = parseStrictPositiveInteger(durationValue);
  if (!purchaseDate || durationYears === null) {
    return { expiry_date: "", status: "" };
  }

  const expiryDate = new Date(purchaseDate);
  expiryDate.setFullYear(expiryDate.getFullYear() + durationYears);

  const dateIn = dateInDateOnly(ticket);
  const dayDifference = Math.floor((dateIn - purchaseDate) / 86400000);
  let status = "out_of_warranty";
  if (dayDifference >= 0 && dayDifference <= 7) {
    status = "doa";
  } else if (dateIn <= expiryDate) {
    status = "in_warranty";
  }

  return {
    expiry_date: formatISODateOnly(expiryDate),
    status,
  };
}

function normalizeString(value) {
  return value === null || value === undefined ? "" : String(value);
}

function normalizeArray(values) {
  return [...new Set(Array.isArray(values) ? values.filter(Boolean) : [])].sort();
}

export function buildEditDraft(ticket) {
  const dvt = ticket?.warranty?.dvt || {};
  const imin = ticket?.warranty?.imin || {};
  return {
    category: normalizeString(ticket?.category),
    branch: normalizeString(ticket?.branch),
    stage: normalizeString(ticket?.stage),
    owner: {
      name: normalizeString(ticket?.owner?.name),
      pic: normalizeString(ticket?.owner?.pic),
      contact: normalizeString(ticket?.owner?.contact),
      address: normalizeString(ticket?.owner?.address),
    },
    device: {
      serial_number: normalizeString(ticket?.device?.serial_number),
      purchase_date: normalizeString(ticket?.device?.purchase_date),
      model_selection: normalizeString(ticket?.device?.model_selection),
      model: normalizeString(ticket?.device?.model),
      stock_reference_status: normalizeString(ticket?.stock_reference_status || ticket?.device?.stock_reference_status),
      sn_check_result: "",
      sn_check_serial_number: normalizeString(ticket?.device?.serial_number),
      sn_check_warning: "",
      purchase_date_sn_message: "",
    },
    warranty: {
      dvt: {
        duration_years: dvt.duration_years ? String(dvt.duration_years) : "1",
        expiry_date: normalizeString(dvt.expiry_date),
        status: normalizeString(dvt.status),
      },
      imin: {
        status: normalizeString(imin.status || "unknown"),
        expiry_date: normalizeString(imin.expiry_date),
        sn_check_message: "",
        lookup_result: "",
      },
    },
    accessories: {
      items: normalizeArray(ticket?.accessories?.items),
      other_details: normalizeString(ticket?.accessories?.other_details),
    },
    problem: normalizeString(ticket?.problem),
    root_cause: normalizeString(ticket?.root_cause),
    resolution: normalizeString(ticket?.resolution),
    pending_reason: normalizeString(ticket?.pending_reason),
    repair_action: normalizeString(ticket?.repair_action),
    technician_id: normalizeString(ticket?.technician_id),
    technician_name: normalizeString(ticket?.technician_name),
    expedition: normalizeString(ticket?.expedition),
    tracking_number: normalizeString(ticket?.tracking_number),
    notes: normalizeString(ticket?.notes),
    void_warranty: dvt.status === "void",
    void_warranty_touched: false,
    void_reason: "",
    unknown_stock_reference_confirmed: false,
    service_cost: serviceCostDraft(ticket?.service_cost),
  };
}

function normalizeDraft(draft) {
  return {
    ...draft,
    stage: normalizeString(draft.stage),
    owner: {
      name: draft.owner.name.trim(),
      pic: draft.owner.pic.trim(),
      contact: draft.owner.contact.trim(),
      address: draft.owner.address.trim(),
    },
    device: {
      serial_number: draft.device.serial_number.trim(),
      purchase_date: draft.device.purchase_date,
      model_selection: draft.device.model_selection,
      model: draft.device.model,
      stock_reference_status: draft.device.stock_reference_status,
    },
    warranty: {
      dvt: {
        duration_years: normalizeString(draft.warranty.dvt.duration_years).trim(),
        expiry_date: draft.warranty.dvt.expiry_date,
        status: draft.warranty.dvt.status,
      },
      imin: {
        status: draft.warranty.imin.status || "unknown",
        expiry_date: draft.warranty.imin.expiry_date || "",
      },
    },
    accessories: {
      items: normalizeArray(draft.accessories.items),
      other_details: draft.accessories.items.includes("other") ? draft.accessories.other_details.trim() : "",
    },
    problem: draft.problem.trim(),
    root_cause: draft.root_cause.trim(),
    resolution: draft.resolution.trim(),
    pending_reason: normalizeString(draft.pending_reason).trim(),
    repair_action: draft.repair_action,
    technician_id: draft.technician_id,
    technician_name: draft.technician_name,
    expedition: draft.expedition.trim(),
    tracking_number: draft.tracking_number.trim(),
    notes: draft.notes.trim(),
  };
}

function sanitizeReturnUrl(value, fallback) {
  if (!value || !value.startsWith("/") || value.startsWith("//")) return fallback;
  try {
    const parsed = new URL(value, "http://flowpi.local");
    if (parsed.origin !== "http://flowpi.local") return fallback;
    if (!parsed.pathname.startsWith("/service/") && parsed.pathname !== "/report") return fallback;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return fallback;
  }
}

function fieldValue(draft, path) {
  return path.split(".").reduce((value, key) => value?.[key], draft);
}

function formatReviewValue(path, value, technicians) {
  if (Array.isArray(value)) {
    return value.map((item) => ACCESSORY_LABELS[item] || item).join(", ") || EMPTY;
  }
  if (path === "branch") return formatLabel(SERVICE_BRANCH_LABELS, value);
  if (path === "category") return formatLabel(SERVICE_CATEGORY_LABELS, value);
  if (path === "stage") return formatLabel(SERVICE_STAGE_LABELS, value);
  if (path === "repair_action") return formatLabel(REPAIR_ACTION_LABELS, value);
  if (path === "technician_id") {
    return technicians.find((technician) => technician.id === value)?.name || value || EMPTY;
  }
  if (path === "warranty.imin.status") return formatLabel(IMIN_STATUS_LABELS, value);
  if (path === "warranty.dvt.status") return formatLabel(SERVICE_DVT_STATUS_LABELS, value);
  return display(value);
}

export function buildChangedFields(original, current, technicians, permissionContext = null) {
  const capabilities = permissionContext
    ? getServiceEditCapabilities(
        permissionContext.role,
        permissionContext.persistedStage ?? original?.stage
      )
    : null;
  const fields = [
    ["category", "Category"],
    ["branch", "Branch"],
    ["stage", "Stage"],
    ["owner.name", "Company / Store Name"],
    ["owner.pic", "PIC"],
    ["owner.contact", "Contact"],
    ["owner.address", "Address"],
    ["device.serial_number", "Serial Number"],
    ["device.purchase_date", "Purchase Date"],
    ["device.model_selection", "Model Selection"],
    ["device.model", "Model"],
    ["device.stock_reference_status", "Stock Reference Status"],
    ["warranty.dvt.duration_years", "DVT Warranty Duration"],
    ["warranty.dvt.expiry_date", "DVT Warranty Exp Date"],
    ["warranty.dvt.status", "DVT Warranty Status"],
    ["warranty.imin.expiry_date", "iMin Warranty Exp Date"],
    ["warranty.imin.status", "iMin Warranty Status"],
    ["accessories.items", "Accessories"],
    ["accessories.other_details", "Other Accessory Details"],
    ["problem", "Problem"],
    ["root_cause", "Root Cause"],
    ["resolution", "Resolution"],
    ["pending_reason", "Pending Reason"],
    ["repair_action", "Repair Action"],
    ["technician_id", "Technician"],
    ["expedition", "Expedition"],
    ["tracking_number", "Tracking Number"],
    ["notes", "Notes"],
  ];

  return fields
    .filter(
      ([path]) =>
        !capabilities ||
        capabilities.canEditGeneralFields ||
        (capabilities.canEditLogisticsFields && LOGISTICS_EDIT_FIELD_PATHS.has(path))
    )
    .map(([path, label]) => {
      const before = fieldValue(original, path);
      const after = fieldValue(current, path);
      const normalizedBefore = Array.isArray(before) ? normalizeArray(before) : before || "";
      const normalizedAfter = Array.isArray(after) ? normalizeArray(after) : after || "";
      if (JSON.stringify(normalizedBefore) === JSON.stringify(normalizedAfter)) return null;
      return {
        path,
        label,
        before: formatReviewValue(path, normalizedBefore, technicians),
        after: formatReviewValue(path, normalizedAfter, technicians),
      };
    })
    .filter(Boolean);
}

export function buildVoidWarrantyReviewChanges({ originalDraft, currentDraft, managedVoidSuffix }) {
  if (!currentDraft?.void_warranty_touched) return [];

  const wasVoid = originalDraft?.void_warranty === true;
  const willBeVoid = currentDraft?.void_warranty === true;
  const oldReason = normalizeString(managedVoidSuffix?.reason);
  const newReason = normalizeString(currentDraft?.void_reason);

  if (!wasVoid && willBeVoid) {
    return [{
      path: "void_warranty.reason",
      label: "Void Reason",
      before: EMPTY,
      after: newReason || EMPTY,
    }];
  }

  if (wasVoid && !willBeVoid) {
    return oldReason ? [{
      path: "void_warranty.reason",
      label: "Void Reason",
      before: oldReason,
      after: EMPTY,
    }] : [];
  }

  if (wasVoid && willBeVoid && oldReason && oldReason !== newReason) {
    return [{
      path: "void_warranty.reason",
      label: "Void Reason",
      before: oldReason,
      after: newReason || EMPTY,
    }];
  }

  return [];
}

function setIfChanged(target, key, originalValue, currentValue, transform = (value) => value) {
  const normalizedOriginal = transform(originalValue);
  const normalizedCurrent = transform(currentValue);
  if (JSON.stringify(normalizedOriginal) !== JSON.stringify(normalizedCurrent)) {
    target[key] = normalizedCurrent;
  }
}

function assignNestedPayload(payload, section, key, value) {
  if (!payload[section]) payload[section] = {};
  payload[section][key] = value;
}

function assignWarrantyPayload(payload, group, key, value) {
  if (!payload.warranty) payload.warranty = {};
  if (!payload.warranty[group]) payload.warranty[group] = {};
  payload.warranty[group][key] = value;
}

function normalizeNullable(value) {
  const text = normalizeString(value).trim();
  return text || null;
}

export function buildTicketUpdatePayload({
  originalDraft,
  currentDraft,
  finalUsedState = { finalUsedHoldIds: [], finalActiveUsageIds: [] },
  reconciliationChanged = false,
  automaticReversal = false,
  selectorReady = false,
  role,
}) {
  const original = normalizeDraft(originalDraft);
  const current = normalizeDraft(currentDraft);
  const payload = {};
  const capabilities = getServiceEditCapabilities(role, original.stage);
  const isAdmin = role === "admin";

  if (!capabilities.canEditTicket) return payload;

  if (!capabilities.canEditGeneralFields) {
    if (!capabilities.canEditLogisticsFields) return payload;
    [
      ["expedition", normalizeNullable],
      ["tracking_number", normalizeNullable],
      ["notes", (value) => value],
    ].forEach(([field, transform]) => {
      if (original[field] !== current[field]) payload[field] = transform(current[field]);
    });
    return payload;
  }

  if (isAdmin) {
    setIfChanged(payload, "category", original.category, current.category);
    setIfChanged(payload, "branch", original.branch, current.branch);
    setIfChanged(payload, "stage", original.stage, current.stage);
  }

  ["name", "pic", "contact", "address"].forEach((field) => {
    if (original.owner[field] !== current.owner[field]) {
      assignNestedPayload(payload, "owner", field, current.owner[field]);
    }
  });

  ["purchase_date"].forEach((field) => {
    if (original.device[field] !== current.device[field]) {
      assignNestedPayload(payload, "device", field, current.device[field]);
    }
  });
  if (isAdmin && original.device.serial_number !== current.device.serial_number) {
    assignNestedPayload(payload, "device", "serial_number", current.device.serial_number);
  }

  if (original.warranty.dvt.duration_years !== current.warranty.dvt.duration_years) {
    assignWarrantyPayload(payload, "dvt", "duration_years", parseStrictPositiveInteger(current.warranty.dvt.duration_years));
  }

  if (original.warranty.imin.status !== current.warranty.imin.status) {
    assignWarrantyPayload(payload, "imin", "status", current.warranty.imin.status || "unknown");
  }
  const originalIminExpiry = original.warranty.imin.expiry_date || "";
  const currentIminExpiry = current.warranty.imin.expiry_date || "";
  if (originalIminExpiry !== currentIminExpiry) {
    assignWarrantyPayload(payload, "imin", "expiry_date", currentIminExpiry || null);
  }

  if (JSON.stringify(original.accessories.items) !== JSON.stringify(current.accessories.items)) {
    assignNestedPayload(payload, "accessories", "items", current.accessories.items);
  }
  if (original.accessories.other_details !== current.accessories.other_details) {
    assignNestedPayload(payload, "accessories", "other_details", current.accessories.other_details);
  }

  ["problem", "root_cause", "resolution", "pending_reason", "notes"].forEach((field) => {
    if (original[field] !== current[field]) payload[field] = current[field];
  });
  [
    ["technician_id", normalizeNullable],
    ["expedition", normalizeNullable],
    ["tracking_number", normalizeNullable],
  ].forEach(([field, transform]) => {
    if (original[field] !== current[field]) payload[field] = transform(current[field]);
  });
  if (isAdmin && original.repair_action !== current.repair_action) {
    payload.repair_action = normalizeNullable(current.repair_action);
  }
  if (isAdmin && currentDraft.void_warranty_touched) {
    payload.void_warranty = currentDraft.void_warranty === true;
    if (currentDraft.void_warranty === true) payload.void_reason = currentDraft.void_reason;
  }

  if (
    isAdmin &&
    current.device.serial_number !== original.device.serial_number &&
    ["unknown", "unavailable"].includes(currentDraft.device.sn_check_result)
  ) {
    payload.unknown_stock_reference_confirmed = currentDraft.unknown_stock_reference_confirmed === true;
  }

  if (
    isAdmin &&
    selectorReady &&
    reconciliationChanged &&
    !automaticReversal &&
    current.repair_action === "part_replace"
  ) {
    payload.final_used_hold_ids = canonicalIds(finalUsedState.finalUsedHoldIds);
    payload.final_active_usage_ids = canonicalIds(finalUsedState.finalActiveUsageIds);
  }

  return payload;
}

export function shouldRenderSparePartSelection({ editMode, persistedRepairAction, draftRepairAction }) {
  return (editMode ? draftRepairAction : persistedRepairAction) === "part_replace";
}

export function serviceFieldErrors(error) {
  const payload = getServiceErrorPayload(error);
  const fieldErrors = payload?.field_errors;
  const mapped = !fieldErrors || typeof fieldErrors !== "object" ? {} : Object.fromEntries(
    Object.entries(fieldErrors)
      .filter(([key, value]) => typeof key === "string" && typeof value === "string")
      .map(([key, value]) => [key, value])
  );
  if (payload?.code === "SERVICE_UNKNOWN_SN_CONFIRMATION_REQUIRED") {
    mapped.unknown_stock_reference_confirmed = "Please confirm/tick.";
  }
  if (payload?.code === "SERVICE_BRANCH_CHANGE_BLOCKED" && !mapped.branch) {
    mapped.branch = payload.message || "Branch cannot be changed while active spare-part records remain.";
  }
  return mapped;
}

export function computeStage3ValidationErrors({
  draft,
  originalDraft,
  ticket,
  techniciansLoading,
  role,
}) {
  const errors = {};
  const capabilities = getServiceEditCapabilities(role, ticket?.stage ?? originalDraft?.stage);
  if (!capabilities.canEditTicket) return errors;

  if (capabilities.canEditGeneralFields) {
    if (!draft.owner.name.trim()) errors["owner.name"] = "Company / Store Name is required.";
    if (!draft.owner.contact.trim()) errors["owner.contact"] = "Contact is required.";

    const purchaseDate = parseISODateOnly(draft.device.purchase_date);
    if (!draft.device.purchase_date) {
      errors["device.purchase_date"] = "Purchase Date is required.";
    } else if (!purchaseDate) {
      errors["device.purchase_date"] = "Purchase Date must be a valid YYYY-MM-DD date.";
    } else if (purchaseDate.getFullYear() < 2020 || purchaseDate.getFullYear() > CURRENT_YEAR) {
      errors["device.purchase_date"] = `Purchase Date must be between 2020 and ${CURRENT_YEAR}.`;
    }
    if (purchaseDate && dateInDateOnly(ticket) < purchaseDate) {
      errors["device.purchase_date"] = "Date In cannot be earlier than Purchase Date.";
    }

    if (parseStrictPositiveInteger(draft.warranty.dvt.duration_years) === null) {
      errors["warranty.dvt.duration_years"] = "DVT Warranty Duration must be a whole number of at least 1 year.";
    }
    if (draft.warranty.imin.expiry_date) {
      const iminExpiry = parseISODateOnly(draft.warranty.imin.expiry_date);
      if (!iminExpiry) {
        errors["warranty.imin.expiry_date"] = "iMin Warranty Exp Date must be a valid YYYY-MM-DD date.";
      } else if (iminExpiry.getFullYear() < 2020 || iminExpiry.getFullYear() > CURRENT_YEAR + 3) {
        errors["warranty.imin.expiry_date"] = `iMin Warranty Exp Date must be between 2020 and ${CURRENT_YEAR + 3}.`;
      }
    }

    if (draft.accessories.items.includes("other") && !draft.accessories.other_details.trim()) {
      errors["accessories.other_details"] = "Other Accessory Details is required.";
    }

    if (!draft.device.serial_number.trim()) {
      errors["device.serial_number"] = "Serial Number is required.";
    }
  }

  const finalStage = role === "admin" ? draft.stage : ticket?.stage;
  const repairSetRequired = ["validation", "delivery", "done"].includes(finalStage);

  if (capabilities.canEditGeneralFields && finalStage === "pending" && !draft.pending_reason.trim()) {
    errors.pending_reason = "Pending Reason is required.";
  }

  if (capabilities.canEditGeneralFields && repairSetRequired) {
    if (!draft.problem.trim()) errors.problem = "Problem is required.";
    if (!draft.root_cause.trim()) errors.root_cause = "Root Cause is required.";
    if (!draft.resolution.trim()) errors.resolution = "Resolution is required.";
    if (!draft.repair_action) errors.repair_action = "Repair Action is required.";
    if (!draft.technician_id) errors.technician_id = "Technician is required.";
  }

  const finalSerialNumber = draft.device.serial_number.trim();
  const originalSerialNumber = originalDraft.device.serial_number.trim();
  if (capabilities.canEditGeneralFields && finalSerialNumber !== originalSerialNumber) {
    if (!draft.device.sn_check_result || draft.device.sn_check_serial_number !== finalSerialNumber) {
      errors["device.sn_check"] = "Generate SN Check for the current Serial Number.";
    }
    if (["unknown", "unavailable"].includes(draft.device.sn_check_result) && !draft.unknown_stock_reference_confirmed) {
      errors.unknown_stock_reference_confirmed = "Please confirm/tick.";
    }
    if (["unknown", "unavailable"].includes(draft.device.sn_check_result) && !draft.device.model_selection) {
      errors["device.model_selection"] = "Model Selection is required.";
    }
  }

  if (capabilities.canEditGeneralFields && techniciansLoading && repairSetRequired) {
    errors.technician_id = "Technicians are still loading.";
  }
  if (
    capabilities.canEditVoidWarranty &&
    draft.void_warranty_touched &&
    draft.void_warranty &&
    !draft.void_reason.trim()
  ) {
    errors.void_reason = "Void Reason is required.";
  }
  if (finalStage === "done") {
    if (!draft.expedition.trim()) errors.expedition = "Expedition is required.";
  }
  return errors;
}

function optionLabel(row) {
  return [row?.part_number, row?.part_name].filter(Boolean).join(" - ") || row?.id || EMPTY;
}

function holdOptionId(row) {
  return row?.hold_id || row?.id || "";
}

function usageOptionId(row) {
  return row?.usage_id || (row?.record_type === "usage" ? row?.id : "") || "";
}

function canonicalIds(values) {
  return [...new Set((values || []).map((value) => normalizeString(value).trim()).filter(Boolean))].sort();
}

function previewOrigin(row) {
  if (row?.preview_origin === "hold" || row?.preview_origin === "usage") return row.preview_origin;
  return usageOptionId(row) ? "usage" : "hold";
}

export function sparePartPreviewId(row) {
  if (row?.preview_id) return row.preview_id;
  const origin = previewOrigin(row);
  const id = origin === "usage" ? usageOptionId(row) : holdOptionId(row);
  return id ? `${origin}:${id}` : "";
}

function withPreviewIdentity(row, origin) {
  const id = origin === "usage" ? usageOptionId(row) : holdOptionId(row);
  return id ? { ...row, preview_origin: origin, preview_id: `${origin}:${id}` } : null;
}

function uniquePreviewRows(rows) {
  const seen = new Set();
  return (rows || []).filter((row) => {
    const id = sparePartPreviewId(row);
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

export function buildSparePartPreview(selector = {}) {
  return {
    holdRows: uniquePreviewRows((selector.hold_side || []).map((row) => withPreviewIdentity(row, "hold")).filter(Boolean)),
    usedRows: uniquePreviewRows(
      (selector.used_side || [])
        .filter((row) => row?.source_type !== "cannibal")
        .map((row) => withPreviewIdentity(row, "usage"))
        .filter(Boolean)
    ),
  };
}

export function moveSparePartPreviewRows(preview, direction, selectedPreviewIds = []) {
  const selected = new Set(selectedPreviewIds);
  const holdRows = uniquePreviewRows(preview?.holdRows || []);
  const usedRows = uniquePreviewRows(preview?.usedRows || []);
  if (direction === "to_used") {
    const moved = holdRows.filter((row) => selected.has(sparePartPreviewId(row)));
    return {
      holdRows: holdRows.filter((row) => !selected.has(sparePartPreviewId(row))),
      usedRows: uniquePreviewRows([...usedRows, ...moved]),
    };
  }
  if (direction === "to_hold") {
    const moved = usedRows.filter((row) => selected.has(sparePartPreviewId(row)));
    return {
      holdRows: uniquePreviewRows([...holdRows, ...moved]),
      usedRows: usedRows.filter((row) => !selected.has(sparePartPreviewId(row))),
    };
  }
  return { holdRows, usedRows };
}

export function deriveFinalUsedState(usedRows = []) {
  return {
    finalUsedHoldIds: canonicalIds(
      usedRows.filter((row) => previewOrigin(row) === "hold").map(holdOptionId)
    ),
    finalActiveUsageIds: canonicalIds(
      usedRows.filter((row) => previewOrigin(row) === "usage").map(usageOptionId)
    ),
  };
}

export function isSameFinalUsedState(initialState, finalState) {
  return (
    JSON.stringify(canonicalIds(initialState?.finalUsedHoldIds)) ===
      JSON.stringify(canonicalIds(finalState?.finalUsedHoldIds)) &&
    JSON.stringify(canonicalIds(initialState?.finalActiveUsageIds)) ===
      JSON.stringify(canonicalIds(finalState?.finalActiveUsageIds))
  );
}

export function shouldPreviewAutomaticReversal(savedRepairAction, finalRepairAction) {
  return savedRepairAction === "part_replace" && ["part_repair", "software_repair", "cancel_service"].includes(finalRepairAction);
}

export function buildAutomaticReversalPreview(initialPreview) {
  return {
    holdRows: uniquePreviewRows([...(initialPreview?.holdRows || []), ...(initialPreview?.usedRows || [])]),
    usedRows: [],
  };
}

export function deriveEffectivePartReplaceReviewState({
  initialPreview,
  draftPreview,
  cannibalSelection,
  finalRepairAction,
}) {
  const finalIsPartReplace = finalRepairAction === "part_replace";
  const effectiveCannibalSelection = finalIsPartReplace
    ? cannibalSelection
    : { ...cannibalSelection, donorId: "", donorLabel: "", itemIds: [], rows: [] };

  return {
    finalPreview: finalIsPartReplace
      ? draftPreview
      : buildAutomaticReversalPreview(initialPreview),
    cannibalSelection: effectiveCannibalSelection,
    donorChanged:
      (effectiveCannibalSelection?.donorId || "") !==
      (cannibalSelection?.savedDonorId || ""),
    finalDonorLabel: effectiveCannibalSelection?.donorLabel || EMPTY,
    suppressDraftAdditions: !finalIsPartReplace,
  };
}

export function buildSparePartReview(initialPreview, finalPreview, automaticReversal = false) {
  const finalUsedIds = new Set((finalPreview?.usedRows || []).map(sparePartPreviewId));
  const initialUsedIds = new Set((initialPreview?.usedRows || []).map(sparePartPreviewId));
  return {
    holdsBecomingUsed: uniquePreviewRows(
      (finalPreview?.usedRows || []).filter(
        (row) => previewOrigin(row) === "hold" && !initialUsedIds.has(sparePartPreviewId(row))
      )
    ),
    usagesReturningToHold: uniquePreviewRows(
      (initialPreview?.usedRows || []).filter(
        (row) => previewOrigin(row) === "usage" && !finalUsedIds.has(sparePartPreviewId(row))
      )
    ),
    automaticReversal,
  };
}

export function buildUnifiedUsedPartChanges(sparePartReview, cannibalSelection, automaticReversal = false) {
  const changes = [];
  (sparePartReview?.holdsBecomingUsed || []).forEach((row) => changes.push({
    key: `inventory-add:${sparePartPreviewId(row)}`,
    source: "Inventory",
    action: "Added",
    label: optionLabel(row),
  }));
  (sparePartReview?.usagesReturningToHold || []).forEach((row) => changes.push({
    key: `inventory-remove:${sparePartPreviewId(row)}`,
    source: "Inventory",
    action: "Removed",
    label: optionLabel(row),
  }));
  const savedRows = cannibalSelection?.savedRows || [];
  const finalRows = automaticReversal ? [] : (cannibalSelection?.rows || []);
  const donorChanged = (cannibalSelection?.donorId || "") !== (cannibalSelection?.savedDonorId || "");
  const savedIds = new Set(donorChanged ? [] : savedRows.map((row) => row.itemId));
  const finalIds = new Set(donorChanged ? [] : finalRows.map((row) => row.itemId));
  finalRows.filter((row) => !savedIds.has(row.itemId)).forEach((row) => changes.push({
    key: `cannibal-add:${row.itemId}`,
    source: "Cannibal",
    action: "Added",
    label: `${row.part_name || "Unnamed spare part"} · ${row.part_number || "-"}`,
  }));
  savedRows.filter((row) => !finalIds.has(row.itemId)).forEach((row) => changes.push({
    key: `cannibal-remove:${row.itemId}`,
    source: "Cannibal",
    action: "Removed",
    label: `${row.part_name || "Unnamed spare part"} · ${row.part_number || "-"}`,
  }));
  return changes;
}

export function canEditSparePartPreview({
  editMode,
  role,
  repairAction,
  selectorLoaded,
  selectorError,
  draftBranch,
  persistedBranch,
  saving,
}) {
  return Boolean(
    editMode &&
      role === "admin" &&
      repairAction === "part_replace" &&
      selectorLoaded &&
      !selectorError &&
      draftBranch === persistedBranch &&
      !saving
  );
}

function Badge({ children, tone = "default" }) {
  const passiveTone = tone === "primary" ? "info" : tone === "warning" ? "warning" : "neutral";
  return <PassiveBadge tone={passiveTone}>{children}</PassiveBadge>;
}

function DetailSection({ number, title, children, className = "" }) {
  return (
    <section className={`overflow-hidden rounded-sm border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-950 ${className}`}>
      <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-4 dark:border-zinc-800">
        <h2 className="text-sm font-semibold text-zinc-950 dark:text-zinc-100">
          {number ? `${number}. ` : ""}{title}
        </h2>
        <span className="text-zinc-400">^</span>
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

function FieldError({ error }) {
  return <p className="mt-1 min-h-[18px] text-xs text-destructive" data-field-error={error ? "true" : "false"}>{error || ""}</p>;
}

function ReadField({ label, value, children, className = "", multiline = false }) {
  return (
    <div className={className}>
      <div className="flex h-4 items-center text-[10px] font-semibold uppercase tracking-[0.08em] text-zinc-500 dark:text-zinc-400">
        {label}
      </div>
      <div
        className={`field-readonly-display mt-2 min-w-0 text-sm ${multiline ? "h-[82px] overflow-y-auto whitespace-pre-wrap break-words" : "break-words"}`}
        data-readonly-presentation={multiline ? "multiline" : "singleline"}
      >
        {children || <DataValue value={value} />}
      </div>
    </div>
  );
}

function TextField({
  label,
  value,
  onChange,
  error,
  readOnly = false,
  disabled = false,
  multiline = false,
  type = "text",
  className = "",
  endAdornment = null,
  labelAdornment = null,
  showAbsent = false,
}) {
  const baseClass =
    "mt-2 h-11 w-full rounded-sm border border-zinc-200 px-3 py-2.5 text-sm text-zinc-900 outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/10 dark:border-zinc-700 dark:text-zinc-100";
  const stateClass = readOnly ? "field-readonly" : "field-editable";
  const disabledClass = disabled ? "cursor-not-allowed opacity-45" : "";
  const inputClass = `${baseClass} ${stateClass} ${disabledClass}`;
  const compoundInputClass = `min-w-0 flex-1 rounded-l-sm border border-r-0 border-zinc-200 px-3 py-2.5 text-sm text-zinc-900 outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/10 ${stateClass} ${disabledClass} dark:border-zinc-700 dark:text-zinc-100`;
  return (
    <div className={className}>
      <div className="flex min-h-4 items-center justify-between gap-3">
        <label className="text-[10px] font-semibold uppercase tracking-[0.08em] text-zinc-600 dark:text-zinc-300">
          {label}
        </label>
        {labelAdornment}
      </div>
      {multiline ? (
        <textarea
          value={readOnly && showAbsent ? displayValue(value) : value || ""}
          onChange={(event) => onChange?.(event.target.value)}
          readOnly={readOnly}
          disabled={disabled}
          className={`${inputClass} min-h-[96px] resize-none`}
        />
      ) : endAdornment ? (
        <div className="mt-2 flex h-11 w-full">
          <input
            type={type}
            value={readOnly && showAbsent ? displayValue(value) : value || ""}
            onChange={(event) => onChange?.(event.target.value)}
            readOnly={readOnly}
            disabled={disabled}
            className={compoundInputClass}
          />
          {endAdornment}
        </div>
      ) : (
        <input
          type={type}
          value={readOnly && showAbsent ? displayValue(value) : value || ""}
          onChange={(event) => onChange?.(event.target.value)}
          readOnly={readOnly}
          disabled={disabled}
          className={inputClass}
        />
      )}
      <FieldError error={error} />
    </div>
  );
}

function CompoundInputControl({ value, onChange, readOnly = false, disabled = false, type = "text", endAdornment = null, className = "" }) {
  const stateClass = readOnly ? "field-readonly" : "field-editable";
  const disabledClass = disabled ? "cursor-not-allowed opacity-45" : "";
  return (
    <div className={`flex h-11 w-full ${className}`}>
      <input
        type={type}
        value={value || ""}
        onChange={(event) => onChange?.(event.target.value)}
        readOnly={readOnly}
        disabled={disabled}
        className={`min-w-0 flex-1 border border-zinc-200 px-3 text-sm text-zinc-900 outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/10 ${
          endAdornment ? "rounded-l-sm border-r-0" : "rounded-sm"
        } ${stateClass} ${disabledClass} dark:border-zinc-700 dark:text-zinc-100`}
      />
      {endAdornment}
    </div>
  );
}

function ReadOnlyDvtBadgeField({ status, error, labelAdornment = null }) {
  return (
    <div>
      <div className="flex min-h-4 items-center justify-between gap-3">
        <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-zinc-600 dark:text-zinc-300">
          DVT Warranty Status
        </span>
        {labelAdornment}
      </div>
      <div className="field-readonly-display mt-2 flex h-11 items-center px-3" data-testid="service-detail-dvt-status-presentation">
        <ServiceDvtBadge status={status} />
      </div>
      <FieldError error={error} />
    </div>
  );
}

function SelectField({ label, value, onChange, error, children, disabled = false, className = "" }) {
  return (
    <div className={className}>
      <div className="flex h-4 items-center">
        <label className="text-[10px] font-semibold uppercase tracking-[0.08em] text-zinc-600 dark:text-zinc-300">
          {label}
        </label>
      </div>
      <select
        value={value || ""}
        onChange={(event) => onChange?.(event.target.value)}
        disabled={disabled}
        className={`mt-2 h-11 w-full rounded-sm border px-3 py-2.5 text-sm outline-none transition-colors ${disabled ? "field-readonly cursor-not-allowed" : "field-editable"}`}
      >
        {children}
      </select>
      <FieldError error={error} />
    </div>
  );
}

function SplitDateField({
  label,
  value,
  onChange,
  error,
  helper,
  helperTestId,
  helperTone,
  readOnly = false,
  disabled = false,
  nullable = false,
  yearOptions = OPERATIONAL_YEAR_OPTIONS,
  className = "",
  endAdornment = null,
}) {
  const parts = splitDateValue(value);
  const isDisabled = readOnly || disabled;
  const selectBase =
    "h-11 min-w-0 border-0 bg-transparent px-2 py-2.5 text-sm text-zinc-900 outline-none transition-colors disabled:cursor-not-allowed disabled:text-zinc-500 dark:text-zinc-100 dark:disabled:text-zinc-400";
  const plainSelectBase = `mt-2 h-11 min-w-0 rounded-sm border px-2 py-2.5 text-sm outline-none transition-colors ${isDisabled ? "field-readonly cursor-not-allowed" : "field-editable"}`;

  const updatePart = (part, nextValue) => {
    if (isDisabled) return;
    const next = { ...parts, [part]: nextValue };
    if (!next.day && !next.month && !next.year) {
      onChange?.("");
      return;
    }
    const year = next.year || "";
    const month = next.month || "";
    const day = next.day || "";
    onChange?.(`${year}-${month}-${day}`);
  };

  const selects = [
    {
      key: "day",
      value: parts.day,
      label: "Day",
      options: DAY_OPTIONS.map((day) => ({ value: day, label: day })),
    },
    { key: "month", value: parts.month, label: "Month", options: MONTH_OPTIONS },
    {
      key: "year",
      value: parts.year,
      label: "Year",
      options: yearOptions.map((year) => ({ value: year, label: year })),
    },
  ];

  return (
    <div className={className}>
      <div className="flex h-4 items-center">
        <label className="text-[10px] font-semibold uppercase tracking-[0.08em] text-zinc-600 dark:text-zinc-300">
          {label}
        </label>
      </div>
      {endAdornment ? (
        <div className="mt-2 flex h-11 w-full">
          <div className={`grid min-w-0 flex-1 grid-cols-3 overflow-hidden rounded-l-sm border border-r-0 ${isDisabled ? "field-readonly" : "field-editable"}`}>
            {selects.map((select, index) => (
              <select
                key={select.key}
                value={select.value}
                onChange={(event) => updatePart(select.key, event.target.value)}
                disabled={isDisabled}
                className={`${selectBase} ${isDisabled ? "field-readonly" : ""} ${index > 0 ? "border-l border-zinc-200 dark:border-zinc-700" : ""}`}
                aria-label={`${label} ${select.label}`}
              >
                <option value="">{nullable ? "-" : select.label}</option>
                {select.options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ))}
          </div>
          {endAdornment}
        </div>
      ) : (
        <div className="grid h-11 grid-cols-3 gap-2">
          {selects.map((select) => (
            <select
              key={select.key}
              value={select.value}
              onChange={(event) => updatePart(select.key, event.target.value)}
              disabled={isDisabled}
              className={plainSelectBase}
              aria-label={`${label} ${select.label}`}
            >
              <option value="">{nullable ? "-" : select.label}</option>
              {select.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          ))}
        </div>
      )}
      <p
        className={`mt-1 min-h-[18px] text-xs ${
          error
            ? "text-destructive"
            : helperTone === "success" || helper === "Purchase Date updated from SN Check."
              ? "text-success"
              : "text-warning"
        }`}
        data-field-error={error ? "true" : "false"}
        data-testid={helper ? helperTestId || "service-detail-purchase-date-message" : undefined}
      >
        {error || helper || ""}
      </p>
    </div>
  );
}

function SegmentedControl({ label, value, options, onChange, readOnly = false, error, testId, columns = options.length }) {
  const multiRow = options.length > columns;
  return (
    <div>
      <div className="flex h-4 items-center text-[10px] font-semibold uppercase tracking-[0.08em] text-zinc-500 dark:text-zinc-400">
        {label}
      </div>
      <div
        className={`mt-2 grid overflow-hidden rounded-sm border border-border bg-muted ${multiRow ? "h-[88px]" : "h-11"}`}
        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
        role="group"
        aria-label={label}
        aria-readonly={readOnly ? "true" : "false"}
        data-testid={testId}
        data-segment-columns={columns}
        data-segment-rows={Math.ceil(options.length / columns)}
      >
        {options.map((option, index) => {
          const selected = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              disabled={readOnly}
              onClick={() => onChange?.(option.value)}
              aria-pressed={selected}
              data-value={option.value}
              className={`flex h-11 min-w-0 items-center justify-center text-xs font-semibold transition-colors focus-visible:relative focus-visible:z-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary ${multiRow ? "gap-1 px-1.5" : "gap-2 px-3"} ${
                readOnly ? "cursor-default" : "hover:bg-accent hover:text-foreground"
              } ${index % columns === 0 ? "border-l-0" : "border-l border-border"} ${index >= columns ? "border-t border-border" : ""} ${
                selected
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground"
              }`}
            >
              <span
                aria-hidden="true"
                className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                  selected ? "border-primary-foreground/70" : "border-muted-foreground"
                }`}
              >
                {selected ? <Check className="h-3 w-3" strokeWidth={3} /> : null}
              </span>
              <span className={multiRow ? "whitespace-nowrap" : "truncate"}>{option.label}</span>
            </button>
          );
        })}
      </div>
      <FieldError error={error} />
    </div>
  );
}

function LockButton({ locked, onClick, disabled, label }) {
  if (disabled) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      className={`inline-flex h-full min-h-[42px] w-11 shrink-0 items-center justify-center rounded-r-sm border transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] ${
        locked
          ? "border-warning/40 bg-warning/15 text-warning hover:bg-warning/20 focus-visible:outline-warning"
          : "border-primary/50 bg-primary/15 text-primary hover:bg-primary/20 focus-visible:outline-primary"
      }`}
      aria-label={label}
      aria-pressed={!locked}
    >
      {locked ? <Lock className="h-4 w-4" strokeWidth={2} /> : <Unlock className="h-4 w-4" strokeWidth={2} />}
    </button>
  );
}

function LockedSegmentedControl({ label, value, options, onChange, locked, onToggle, disabled = false, error, testId, columns = options.length }) {
  const multiRow = options.length > columns;
  return (
    <div>
      <div className="flex h-4 items-center text-[10px] font-semibold uppercase tracking-[0.08em] text-zinc-500 dark:text-zinc-400">
        {label}
      </div>
      <div className={`mt-2 flex w-full ${multiRow ? "h-[88px]" : "h-11"}`}>
        <div
          className="grid min-w-0 flex-1 overflow-hidden rounded-l-sm border border-r-0 border-border bg-muted"
          style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
          role="group"
          aria-label={label}
          data-testid={testId}
          data-segment-columns={columns}
          data-segment-rows={Math.ceil(options.length / columns)}
        >
          {options.map((option, index) => {
            const selected = option.value === value;
            return (
              <button
                key={option.value}
                type="button"
                disabled={locked}
                onClick={() => onChange?.(option.value)}
                aria-pressed={selected}
                data-value={option.value}
                className={`flex h-11 min-w-0 items-center justify-center text-xs font-semibold transition-colors focus-visible:relative focus-visible:z-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary ${multiRow ? "gap-1 px-1.5" : "gap-2 px-3"} ${
                  locked ? "cursor-not-allowed" : "hover:bg-accent hover:text-foreground"
                } ${index % columns === 0 ? "border-l-0" : "border-l border-border"} ${index >= columns ? "border-t border-border" : ""} ${
                  selected
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                <span
                  aria-hidden="true"
                  className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                    selected ? "border-primary-foreground/70" : "border-muted-foreground"
                  }`}
                >
                  {selected ? <Check className="h-3 w-3" strokeWidth={3} /> : null}
                </span>
                <span className={multiRow ? "whitespace-nowrap" : "truncate"}>{option.label}</span>
              </button>
            );
          })}
        </div>
        <LockButton locked={locked} onClick={onToggle} disabled={disabled} label={locked ? `Unlock ${label}` : `Lock ${label}`} />
      </div>
      <FieldError error={error} />
    </div>
  );
}

function AccessoryEditor({ draft, updateDraft, errors }) {
  const [clearOtherOpen, setClearOtherOpen] = useState(false);
  const otherSelected = draft.accessories.items.includes("other");
  const applyToggle = (value) => {
    updateDraft((current) => {
      const selected = current.accessories.items.includes(value);
      const nextItems = selected
        ? current.accessories.items.filter((item) => item !== value)
        : [...current.accessories.items, value];
      return {
        ...current,
        accessories: {
          items: normalizeArray(nextItems),
          other_details: nextItems.includes("other") ? current.accessories.other_details : "",
        },
      };
    });
  };
  const toggleAccessory = (value) => {
    if (value === "other" && otherSelected && draft.accessories.other_details.trim()) {
      setClearOtherOpen(true);
      return;
    }
    applyToggle(value);
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-2">
        {ACCESSORY_OPTIONS.map((option) => {
          const checked = draft.accessories.items.includes(option.value);
          return (
            <label key={option.value} className="flex cursor-pointer items-center gap-2 text-sm text-zinc-700 dark:text-zinc-300">
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggleAccessory(option.value)}
                className="h-4 w-4 accent-primary"
              />
              {option.label}
            </label>
          );
        })}
      </div>
      <FieldError error={errors["accessories.items"]} />
      <FlowpiAlertDialog
        open={clearOtherOpen}
        onClose={() => setClearOtherOpen(false)}
        title="Clear Other Accessory Details?"
        description="The entered accessory details will be cleared."
        footer={(
          <>
            <Button variant="cancel" onClick={() => setClearOtherOpen(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => {
                applyToggle("other");
                setClearOtherOpen(false);
              }}
            >
              Clear Details
            </Button>
          </>
        )}
        testId="clear-other-accessory-dialog"
      >
        Clear the current Other Accessory Details and deselect Other?
      </FlowpiAlertDialog>
      <div
        className={`overflow-hidden transition-all duration-200 ease-out ${
          otherSelected
            ? "max-h-32 translate-y-0 opacity-100"
            : "max-h-0 -translate-y-1 opacity-0"
        }`}
        aria-hidden={!otherSelected}
      >
        <TextField
          label="Other Accessory Details"
          value={draft.accessories.other_details}
          disabled={!otherSelected}
          onChange={(value) => updateDraft((current) => ({
            ...current,
            accessories: { ...current.accessories, other_details: value },
          }))}
          error={errors["accessories.other_details"]}
        />
      </div>
    </div>
  );
}

function AccessoryReadOnly({ items }) {
  const selected = new Set(items || []);
  if (selected.size === 0) {
    return <div className="field-readonly-display text-sm">{EMPTY}</div>;
  }
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-2">
      {ACCESSORY_OPTIONS.map((option) => {
        const checked = selected.has(option.value);
        return (
          <div key={option.value} className="flex items-center gap-2 text-sm text-zinc-700 dark:text-zinc-300">
            <span
              className={`inline-flex h-4 w-4 items-center justify-center rounded-sm border text-[10px] ${
                checked
                  ? "border-success bg-success text-success-foreground"
                  : "border-zinc-300 bg-white dark:border-zinc-700 dark:bg-zinc-950"
              }`}
            >
              {checked ? "x" : ""}
            </span>
            {option.label}
          </div>
        );
      })}
    </div>
  );
}

export function SparePartPanel({
  title,
  rows,
  editMode,
  selectedIds = [],
  onToggle,
  actionLabel,
  onAction,
  actionDisabled,
  used = false,
}) {
  const columns = editMode
    ? "grid-cols-[44px_minmax(110px,0.8fr)_minmax(180px,1.2fr)]"
    : "grid-cols-[minmax(120px,0.75fr)_minmax(200px,1.25fr)]";

  return (
    <div className="overflow-hidden rounded-sm border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-950" data-testid="hold-part-ledger">
      <div className={`sticky top-0 z-10 grid ${columns} items-center border-b border-zinc-200 bg-zinc-50 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-zinc-600 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400`} data-testid="hold-part-header">
        {editMode ? <div /> : null}
        <div>Model</div>
        <div className="flex items-center justify-between gap-2">
          <span>{title}</span>
          {editMode ? (
            <Button
              type="button"
              disabled={actionDisabled}
              onClick={onAction}
              variant={used ? "transfer-remove" : "transfer-add"}
              size="sm"
              data-action-category={used ? "transfer-remove" : "transfer-add"}
            >
              {actionLabel}
            </Button>
          ) : null}
        </div>
      </div>
      <div className="max-h-[238px] min-h-[210px] overflow-y-auto">
        {rows.length > 0 ? (
          rows.map((row) => {
            const rowId = sparePartPreviewId(row);
            const checked = selectedIds.includes(rowId);
            return (
              <div
                key={rowId}
                className={`grid ${columns} border-b border-zinc-100 px-3 py-3 text-sm last:border-b-0 dark:border-zinc-800`}
                data-testid="hold-part-row"
              >
                {editMode ? (
                  <div>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => onToggle?.(rowId)}
                      disabled={!onToggle}
                      className="h-5 w-5 rounded-sm accent-primary disabled:cursor-not-allowed"
                    />
                  </div>
                ) : null}
                <div className="text-xs font-semibold tracking-[0.02em] text-zinc-700 dark:text-zinc-300">
                  {display(row.model)}
                </div>
                <div>
                  <div className="font-medium text-zinc-900 dark:text-zinc-100">
                    {display(row.part_name)}
                  </div>
                  <div className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
                    {display(row.part_number)}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="px-3 py-10 text-center text-sm text-zinc-500 dark:text-zinc-400">
            No {title.toLowerCase()} records.
          </div>
        )}
      </div>
    </div>
  );
}

export function ReviewChangesModal({
  open,
  onClose,
  onConfirm,
  changedFields,
  usedPartChanges,
  automaticReversal,
  saving,
  error,
  confirmDisabled,
  serviceCostMutation,
  serviceCostPreview,
  originalServiceCost,
  serviceCostDraft,
}) {
  if (!open) return null;
  return (
    <FlowpiDialogSurface
      open
      onClose={onClose}
      busy={saving}
      accessibleTitle="Review Changes"
      testId="service-review-changes-dialog"
    >
      <div className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-sm border border-zinc-200 bg-white shadow-2xl dark:border-zinc-800 dark:bg-zinc-950">
        <div className="flex items-start justify-between border-b border-zinc-200 px-6 py-4 dark:border-zinc-800">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-primary">
              Service Edit
            </div>
            <h2 className="mt-1 text-xl font-semibold text-zinc-950 dark:text-zinc-100">
              Review Changes
            </h2>
          </div>
          <Button
            type="button"
            onClick={onClose}
            disabled={saving}
            variant="ghost"
            size="icon"
            data-action-category="close"
            aria-label="Close Review Changes"
          >
            <X className="h-5 w-5" strokeWidth={2} />
          </Button>
        </div>
        <div className="space-y-6 overflow-y-auto p-6">
          <FormError>{error}</FormError>
          <section>
            <h3 className="text-sm font-semibold text-zinc-950 dark:text-zinc-100">Fields Changed</h3>
            <div className="mt-3 overflow-hidden rounded-sm border border-zinc-200 dark:border-zinc-800">
              <table className="w-full text-left text-sm">
                <thead className="bg-zinc-50 text-[10px] uppercase tracking-[0.08em] text-zinc-500 dark:bg-zinc-900">
                  <tr>
                    <th className="px-3 py-2">Field</th>
                    <th className="px-3 py-2">Before</th>
                    <th className="px-3 py-2">After</th>
                  </tr>
                </thead>
                <tbody>
                  {changedFields.length > 0 ? (
                    changedFields.map((field) => (
                      <tr key={field.path} className="border-t border-zinc-100 dark:border-zinc-800">
                        <td className="px-3 py-2 font-medium">{field.label}</td>
                        <td className="px-3 py-2 text-zinc-500">{field.before}</td>
                        <td className="px-3 py-2">{field.after}</td>
                      </tr>
                    ))
                  ) : usedPartChanges.length > 0 ? (
                    <tr>
                      <td colSpan="3" className="px-3 py-6 text-center text-zinc-500">
                        No other field changes.
                      </td>
                    </tr>
                  ) : (
                    <tr>
                      <td colSpan="3" className="px-3 py-6 text-center text-zinc-500">
                        No field changes.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {usedPartChanges.length > 0 ? <section>
            <h3 className="text-sm font-semibold text-zinc-950 dark:text-zinc-100">Used Part Changes</h3>
            <ul className="mt-3 min-h-[84px] space-y-2 rounded-sm border border-zinc-200 p-3 text-sm dark:border-zinc-800" data-testid="review-used-part-changes">
              {usedPartChanges.map((change) => (
                <li key={change.key} className="flex flex-wrap items-center gap-2">
                  <span className={change.source === "Cannibal" ? "text-warning" : "text-primary"}>[{change.source}]</span>
                  <span
                    className={`font-medium ${change.action === "Added" ? "text-semantic-success-soft" : change.action === "Removed" ? "text-semantic-danger-soft" : ""}`}
                    data-review-action={change.action}
                  >
                    {change.action}
                  </span>
                  <span>{change.label}</span>
                </li>
              ))}
            </ul>
          </section> : null}
          {automaticReversal && usedPartChanges.length > 0 ? (
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              Usage history will be preserved. The change is applied only after Confirm & Save succeeds.
            </p>
          ) : null}
          <ServiceCostReview mutation={serviceCostMutation} preview={serviceCostPreview} original={originalServiceCost} draft={serviceCostDraft} />
        </div>
        <div className="flex items-center justify-between border-t border-zinc-200 px-6 py-4 dark:border-zinc-800">
          <Button
            type="button"
            onClick={onClose}
            disabled={saving}
            variant="navigation-back"
            data-action-category="navigation-back"
          >
            Back to Edit
          </Button>
          <div className="text-right">
            <Button
              type="button"
              onClick={onConfirm}
              disabled={confirmDisabled}
              loading={saving}
              loadingLabel="Saving..."
              variant="success"
              data-action-category="submit-confirm"
            >
              Confirm & Save
            </Button>
            <p className="mt-1 text-xs text-zinc-500">
              {saving ? "Saving atomic Service ticket changes." : "This will apply the reviewed changes atomically."}
            </p>
          </div>
        </div>
      </div>
    </FlowpiDialogSurface>
  );
}

export default function ServiceTicketDetail() {
  const { ticketId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditRoute = location.pathname.endsWith("/edit");
  const isAdmin = user?.role === "admin";
  const snCheckRequestIdRef = useRef(0);
  const saveRequestInFlightRef = useRef(false);
  const historyTriggerRef = useRef(null);
  const [ticket, setTicket] = useState(null);
  const isDeleted = ticket?.is_deleted === true;
  const editCapabilities = getServiceEditCapabilities(user?.role, ticket?.stage);
  const canEditTicket = editCapabilities.canEditTicket && !isDeleted;
  const canRestoreTicket = isDeleted && canRestoreServiceTicket(user?.role);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editMode, setEditMode] = useState(isEditRoute && canEditTicket);
  const [editDraft, setEditDraft] = useState(buildEditDraft(null));
  const [branchLocked, setBranchLocked] = useState(true);
  const [categoryLocked, setCategoryLocked] = useState(true);
  const [stageLocked, setStageLocked] = useState(true);
  const [serialLocked, setSerialLocked] = useState(true);
  const [purchaseDateLocked, setPurchaseDateLocked] = useState(true);
  const [durationLocked, setDurationLocked] = useState(true);
  const [formErrors, setFormErrors] = useState({});
  const [reviewOpen, setReviewOpen] = useState(false);
  const [snCheckLoading, setSnCheckLoading] = useState(false);
  const [spareParts, setSpareParts] = useState({ hold_side: [], used_side: [] });
  const [cannibalSelection, setCannibalSelection] = useState({ donorId: "", itemIds: [], loaded: false, changed: false, error: "", loading: false });
  const [pendingRepairAction, setPendingRepairAction] = useState(null);
  const [sparePartsLoading, setSparePartsLoading] = useState(false);
  const [sparePartsLoaded, setSparePartsLoaded] = useState(false);
  const [sparePartsError, setSparePartsError] = useState("");
  const [holdPreviewRows, setHoldPreviewRows] = useState([]);
  const [usedPreviewRows, setUsedPreviewRows] = useState([]);
  const [selectedHoldIds, setSelectedHoldIds] = useState([]);
  const [selectedUsageIds, setSelectedUsageIds] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [techniciansLoading, setTechniciansLoading] = useState(false);
  const [techniciansError, setTechniciansError] = useState("");
  const [deviceModels, setDeviceModels] = useState([]);
  const [deviceModelsLoading, setDeviceModelsLoading] = useState(false);
  const [deviceModelsError, setDeviceModelsError] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyRecords, setHistoryRecords] = useState([]);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [notice, setNotice] = useState("");
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreError, setRestoreError] = useState("");
  const [pendingDiscardAction, setPendingDiscardAction] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function loadTicket() {
      setLoading(true);
      setError("");
      try {
        const response = await getServiceTicket(ticketId);
        if (cancelled) return;
        const loaded = normalizeServiceTicketResponse(response);
        if (!loaded) throw new Error("Service ticket response was malformed.");
        setTicket(loaded);
        setEditDraft(buildEditDraft(loaded));
      } catch (err) {
        if (cancelled) return;
        setTicket(null);
        setError(formatApiError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadTicket();

    return () => {
      cancelled = true;
    };
  }, [ticketId]);

  useEffect(() => {
    let cancelled = false;

    async function loadSpareParts() {
      setSparePartsLoading(true);
      setSparePartsLoaded(false);
      setSparePartsError("");
      try {
        const response = await getServiceSparePartOptions(ticketId);
        if (cancelled) return;
        setSpareParts(normalizeServiceSelectorResponse(response));
        setSparePartsLoaded(true);
      } catch (err) {
        if (cancelled) return;
        setSpareParts({ hold_side: [], used_side: [] });
        setSparePartsLoaded(false);
        setSparePartsError(formatApiError(err));
      } finally {
        if (!cancelled) setSparePartsLoading(false);
      }
    }

    if (ticketId && ticket && !isDeleted) {
      loadSpareParts();
    } else if (isDeleted) {
      setSpareParts({ hold_side: [], used_side: [] });
      setSparePartsLoaded(true);
      setSparePartsError("");
      setSparePartsLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, [isDeleted, ticket, ticketId]);

  useEffect(() => {
    let cancelled = false;

    async function loadTechnicians() {
      setTechniciansLoading(true);
      setTechniciansError("");
      try {
        const response = await listActiveServiceTechnicians();
        if (cancelled) return;
        setTechnicians(unwrapTechnicians(response).filter((technician) => technician?.is_active === true));
      } catch (err) {
        if (cancelled) return;
        setTechnicians([]);
        setTechniciansError(formatApiError(err));
      } finally {
        if (!cancelled) setTechniciansLoading(false);
      }
    }

    if (isEditRoute && canEditTicket && ticket) loadTechnicians();

    return () => {
      cancelled = true;
    };
  }, [canEditTicket, isEditRoute, ticket]);

  useEffect(() => {
    let cancelled = false;

    async function loadDeviceModels() {
      setDeviceModelsLoading(true);
      setDeviceModelsError("");
      try {
        const models = await listActiveServiceDeviceModels();
        if (!cancelled) setDeviceModels(models);
      } catch (err) {
        if (!cancelled) {
          setDeviceModels([]);
          setDeviceModelsError(formatApiError(err));
        }
      } finally {
        if (!cancelled) setDeviceModelsLoading(false);
      }
    }

    if (isAdmin && isEditRoute && canEditTicket && ticket) {
      loadDeviceModels();
    }

    return () => {
      cancelled = true;
    };
  }, [canEditTicket, isAdmin, isEditRoute, ticket]);

  useEffect(() => {
    setEditMode(isEditRoute && canEditTicket);
    if (ticket) {
      const draft = buildEditDraft(ticket);
      setEditDraft(draft);
      setFormErrors({});
      setBranchLocked(true);
      setCategoryLocked(true);
      setStageLocked(true);
      setSerialLocked(true);
      setPurchaseDateLocked(true);
      setDurationLocked(true);
      snCheckRequestIdRef.current += 1;
      setSnCheckLoading(false);
      setReviewOpen(false);
      setSaveLoading(false);
      setSaveError("");
    }
  }, [canEditTicket, isEditRoute, ticket, user?.role]);

  const initialSparePartPreview = useMemo(() => buildSparePartPreview(spareParts), [spareParts]);
  const automaticReversalPreview = Boolean(
    editCapabilities.canEditRepairAction &&
      shouldPreviewAutomaticReversal(ticket?.repair_action, editDraft.repair_action)
  );

  useEffect(() => {
    setHoldPreviewRows(initialSparePartPreview.holdRows);
    setUsedPreviewRows(initialSparePartPreview.usedRows);
    setSelectedHoldIds([]);
    setSelectedUsageIds([]);
  }, [
    editDraft.branch,
    initialSparePartPreview,
    isEditRoute,
    user?.role,
  ]);

  const originalDraft = useMemo(() => buildEditDraft(ticket), [ticket]);
  const normalizedDvtPreview = useMemo(
    () => calculateDvtPreview(editDraft.device.purchase_date, editDraft.warranty.dvt.duration_years, ticket),
    [editDraft.device.purchase_date, editDraft.warranty.dvt.duration_years, ticket]
  );
  const effectiveDraft = useMemo(
    () => ({
      ...editDraft,
      warranty: {
        ...editDraft.warranty,
        dvt: {
          ...editDraft.warranty.dvt,
          expiry_date: normalizedDvtPreview.expiry_date || editDraft.warranty.dvt.expiry_date,
          status: editDraft.void_warranty
            ? "void"
            : normalizedDvtPreview.status || editDraft.warranty.dvt.status,
        },
      },
    }),
    [editDraft, normalizedDvtPreview]
  );
  const initialFinalUsedState = useMemo(
    () => deriveFinalUsedState(initialSparePartPreview.usedRows),
    [initialSparePartPreview]
  );
  const finalUsedState = useMemo(() => deriveFinalUsedState(usedPreviewRows), [usedPreviewRows]);
  const branchCompatibleWithSelector = editDraft.branch === ticket?.branch;
  const reconciliationChanged = Boolean(
    editCapabilities.canEditSpareParts &&
      sparePartsLoaded &&
      !sparePartsError &&
      branchCompatibleWithSelector &&
      editDraft.repair_action === "part_replace" &&
      !automaticReversalPreview &&
      !isSameFinalUsedState(initialFinalUsedState, finalUsedState)
  );
  const basePendingUpdatePayload = buildTicketUpdatePayload({
    originalDraft,
    currentDraft: effectiveDraft,
    finalUsedState,
    reconciliationChanged,
    automaticReversal: automaticReversalPreview,
    selectorReady: sparePartsLoaded && !sparePartsError && branchCompatibleWithSelector,
    role: user?.role,
  });
  const cannibalCorrectionAllowed = ["admin", "staff"].includes(user?.role) &&
    editDraft.repair_action === "part_replace";
  const pendingUpdatePayload = { ...basePendingUpdatePayload };
  const canEditServiceCost = !isDeleted && canMutateServiceCost(user?.role, ticket?.stage);
  const directServiceCostMutation = useMemo(
    () => canEditServiceCost
      ? buildServiceCostMutation(ticket?.service_cost, editDraft.service_cost, ticket?.updated_at)
      : {},
    [canEditServiceCost, editDraft.service_cost, ticket?.service_cost, ticket?.updated_at]
  );
  const systemDiscountReview = useMemo(() => {
    return projectSystemManagedDiscount({
      originalDiscount: ticket?.service_cost?.discount,
      originalCategory: ticket?.category,
      finalCategory: effectiveDraft.category,
      originalDvtWarrantyStatus: ticket?.warranty?.dvt?.status,
      finalDvtWarrantyStatus: effectiveDraft.warranty.dvt.status,
      hasDirectDiscountMutation: Boolean(directServiceCostMutation.discount),
    });
  }, [directServiceCostMutation.discount, effectiveDraft.category, effectiveDraft.warranty.dvt.status, ticket?.category, ticket?.service_cost?.discount, ticket?.warranty?.dvt?.status]);
  const effectiveServiceCostDraft = useMemo(
    () => systemDiscountReview
      ? { ...editDraft.service_cost, discount: systemDiscountReview }
      : editDraft.service_cost,
    [editDraft.service_cost, systemDiscountReview]
  );
  const serviceCostPreview = useMemo(
    () => deriveServiceCostDraftPreview(ticket?.service_cost, effectiveServiceCostDraft),
    [ticket?.service_cost, effectiveServiceCostDraft]
  );
  if (editMode && canEditServiceCost) {
    Object.assign(
      pendingUpdatePayload,
      directServiceCostMutation
    );
  }
  if (editMode && cannibalCorrectionAllowed) {
    if (ticket?.repair_action !== editDraft.repair_action) {
      pendingUpdatePayload.repair_action = editDraft.repair_action || null;
    }
    if (editDraft.repair_action === "part_replace" && cannibalSelection.changed) {
      pendingUpdatePayload.cannibal_device_id = cannibalSelection.donorId;
      pendingUpdatePayload.final_cannibal_item_ids = [...new Set(cannibalSelection.itemIds || [])].sort();
    }
  }
  const hasUnsavedChanges =
    editMode && !saveLoading && Object.keys(pendingUpdatePayload).length > 0;
  const reviewValidationErrors = useMemo(
    () =>
      computeStage3ValidationErrors({
        draft: editDraft,
        originalDraft,
        ticket,
        techniciansLoading,
        role: user?.role,
      }),
    [editDraft, originalDraft, ticket, techniciansLoading, user?.role]
  );

  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;
    const handler = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsavedChanges]);

  const updateDraft = (updater) => {
    setEditDraft((current) => {
      const next = typeof updater === "function" ? updater(current) : updater;
      return next;
    });
  };

  const setPath = (path, value) => {
    updateDraft((current) => {
      const next = {
        ...current,
        owner: { ...current.owner },
        device: { ...current.device },
        warranty: {
          dvt: { ...current.warranty.dvt },
          imin: { ...current.warranty.imin },
        },
        accessories: {
          items: [...current.accessories.items],
          other_details: current.accessories.other_details,
        },
      };
      const keys = path.split(".");
      let target = next;
      keys.slice(0, -1).forEach((key) => {
        target = target[key];
      });
      target[keys[keys.length - 1]] = value;

      if (path === "device.serial_number") {
        if (value === originalDraft.device.serial_number) {
          next.device.serial_number = originalDraft.device.serial_number;
          next.device.sn_check_serial_number = originalDraft.device.serial_number;
          next.device.sn_check_result = "";
          next.device.sn_check_warning = "";
          next.device.purchase_date_sn_message = "";
          next.warranty.imin.sn_check_message = "";
          next.warranty.imin.lookup_result = "";
          next.device.stock_reference_status = originalDraft.device.stock_reference_status;
          next.device.model_selection = originalDraft.device.model_selection;
          next.device.model = originalDraft.device.model;
          next.unknown_stock_reference_confirmed = false;
        } else {
          next.device.sn_check_result = "";
          next.device.sn_check_serial_number = "";
          next.device.sn_check_warning = "";
          next.device.purchase_date_sn_message = "";
          next.warranty.imin.sn_check_message = "";
          next.warranty.imin.lookup_result = "";
          next.device.stock_reference_status = "";
          next.device.model_selection = "";
          next.device.model = "";
          next.unknown_stock_reference_confirmed = false;
        }
      }

      if (["warranty.imin.expiry_date", "warranty.imin.status"].includes(path)) {
        next.warranty.imin.sn_check_message = "";
        next.warranty.imin.lookup_result = "";
      }

      if (path === "device.model_selection" && next.device.sn_check_result !== "known") {
        const options = deviceModelOptionsWithHistory(
          deviceModels,
          originalDraft.device.model_selection,
          originalDraft.device.model
        );
        next.device.model = modelNameForSelection(options, value);
      }

      if (path === "device.purchase_date") {
        next.device.purchase_date_sn_message = "";
      }

      return next;
    });
    setFormErrors((current) => {
      if (!current[path]) return current;
      const next = { ...current };
      delete next[path];
      return next;
    });
    if (path === "device.serial_number") {
      snCheckRequestIdRef.current += 1;
      setSnCheckLoading(false);
      setFormErrors((current) => {
        const next = { ...current };
        delete next["device.serial_number"];
        delete next["device.sn_check"];
        delete next["device.sn_check_api"];
        delete next.unknown_stock_reference_confirmed;
        return next;
      });
    }
  };

  const updateVoidWarranty = ({ enabled, reason }) => {
    setEditDraft((current) => ({
      ...current,
      void_warranty: enabled,
      void_warranty_touched: true,
      void_reason: enabled ? reason : "",
    }));
    setFormErrors((current) => {
      const { void_reason: _reason, void_warranty: _warranty, ...rest } = current;
      return rest;
    });
  };

  const continueAfterDiscardCheck = (action) => {
    if (saveLoading) return;
    if (!hasUnsavedChanges) {
      action();
      return;
    }
    setPendingDiscardAction(() => action);
  };

  const viewRoute = () => `/service/tickets/${encodeURIComponent(ticketId)}${location.search}`;

  const startEdit = () => {
    setNotice("");
    navigate(`/service/tickets/${encodeURIComponent(ticketId)}/edit${location.search}`);
  };

  const cancelEdit = () => {
    continueAfterDiscardCheck(() => {
      setNotice("");
      setEditDraft(buildEditDraft(ticket));
      navigate(viewRoute());
    });
  };

  const backToList = () => {
    continueAfterDiscardCheck(() => {
      const params = new URLSearchParams(location.search);
      const fallback = `/service/${ticket?.category || "sold"}`;
      navigate(sanitizeReturnUrl(params.get("return"), fallback));
    });
  };

  const openStageHistory = async () => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    setHistoryError("");
    try {
      const response = await getServiceTicketStageHistory(ticketId, { page: 1, page_size: 100 });
      setHistoryRecords(normalizeServiceStageHistory(response));
    } catch (err) {
      setHistoryRecords([]);
      setHistoryError(formatApiError(err));
    } finally {
      setHistoryLoading(false);
    }
  };

  const closeStageHistory = () => {
    setHistoryOpen(false);
    requestAnimationFrame(() => historyTriggerRef.current?.focus());
  };

  const closeRestore = () => {
    setRestoreOpen(false);
    setRestoreError("");
  };

  const confirmRestore = async (reason) => {
    setRestoreError("");
    try {
      await restoreServiceTicket(ticketId, { reason });
      const activeParams = new URLSearchParams();
      activeParams.set("branch", ticket?.branch || "jakarta");
      activeParams.set("stage", ticket?.stage || "incoming");
      activeParams.set("page", "1");
      const activeListUrl = `/service/${ticket?.category || "sold"}?${activeParams.toString()}`;
      closeRestore();
      navigate(activeListUrl, { replace: true });
    } catch (err) {
      setRestoreError(formatApiError(err));
      throw err;
    }
  };

  const handleGenerateSnCheck = async () => {
    const errors = {};
    const requestedSerialNumber = editDraft.device.serial_number.trim();
    if (!requestedSerialNumber) errors["device.serial_number"] = "Serial Number is required.";
    if (Object.keys(errors).length > 0) {
      setFormErrors((current) => ({ ...current, ...errors }));
      return;
    }

    const requestId = snCheckRequestIdRef.current + 1;
    snCheckRequestIdRef.current = requestId;
    setSnCheckLoading(true);
    updateDraft((current) => ({
      ...current,
      device: { ...current.device, purchase_date_sn_message: "" },
      warranty: {
        ...current.warranty,
        imin: {
          ...current.warranty.imin,
          sn_check_message: "",
          lookup_result: "",
        },
      },
    }));
    setFormErrors((current) => {
      const next = { ...current };
      delete next["device.sn_check"];
      delete next["device.sn_check_api"];
      delete next.unknown_stock_reference_confirmed;
      return next;
    });

    try {
      const response = await checkServiceSerialNumber({ serial_number: requestedSerialNumber });
      if (snCheckRequestIdRef.current !== requestId) return;
      const result = response?.data || {};
      const snResult = result.result;
      const stockReferenceStatus = result.stock_reference_status || "unknown";
      const apiModel = result.model || "";
      const warning = result.warning || response?.message || "";
      const suggestedPurchaseDate = result.suggested_purchase_date || "";
      const iminWarranty = result.imin_warranty;

      updateDraft((current) => {
        const next = {
          ...current,
          device: {
            ...current.device,
            stock_reference_status: stockReferenceStatus,
            sn_check_result: snResult,
            sn_check_serial_number: requestedSerialNumber,
            sn_check_warning: warning,
          },
          unknown_stock_reference_confirmed: false,
        };
        if (snResult === "known") {
          next.device.model_selection = "other";
          next.device.model = apiModel;
          next.device.stock_reference_status = "known";
          if (suggestedPurchaseDate) {
            next.device.purchase_date = suggestedPurchaseDate;
            next.device.purchase_date_sn_message = "Purchase Date updated from SN Check.";
          } else {
            next.device.purchase_date_sn_message = "Purchase Date is not available from SN Check.";
          }
        } else {
          next.device.stock_reference_status = "unknown";
          next.device.model = next.device.model_selection
            ? modelNameForSelection(
                deviceModelOptionsWithHistory(
                  deviceModels,
                  originalDraft.device.model_selection,
                  originalDraft.device.model
                ),
                next.device.model_selection
              )
            : "";
          next.device.purchase_date_sn_message = "";
        }
        if (
          iminWarranty?.lookup_result === "found" &&
          iminWarranty.expiry_date &&
          ["valid", "invalid"].includes(iminWarranty.status)
        ) {
          next.warranty = {
            ...current.warranty,
            imin: {
              ...current.warranty.imin,
              expiry_date: iminWarranty.expiry_date,
              status: iminWarranty.status,
              lookup_result: "found",
              sn_check_message: "iMin warranty fields updated from Generate.",
            },
          };
        } else if (iminWarranty?.lookup_result === "not_found") {
          next.warranty = {
            ...current.warranty,
            imin: {
              ...current.warranty.imin,
              lookup_result: "not_found",
              sn_check_message: "iMin warranty data was not found.",
            },
          };
        } else if (iminWarranty?.lookup_result === "unavailable") {
          next.warranty = {
            ...current.warranty,
            imin: {
              ...current.warranty.imin,
              lookup_result: "unavailable",
              sn_check_message: "iMin warranty lookup is unavailable.",
            },
          };
        }
        return next;
      });
    } catch (err) {
      if (snCheckRequestIdRef.current !== requestId) return;
      setFormErrors((current) => ({ ...current, "device.sn_check_api": formatApiError(err) }));
      updateDraft((current) => ({
        ...current,
        device: { ...current.device, purchase_date_sn_message: "" },
      }));
    } finally {
      if (snCheckRequestIdRef.current === requestId) setSnCheckLoading(false);
    }
  };

  const applyRepairActionChange = (value) => {
    setSelectedHoldIds([]);
    setSelectedUsageIds([]);
    setPath("repair_action", value);
    setPendingRepairAction(null);
  };

  const handleRepairActionChange = (value) => {
    const inventoryAffected = Boolean(
      holdPreviewRows.length || usedPreviewRows.length || reconciliationChanged
    );
    if (editDraft.repair_action === "part_replace" && value !== "part_replace" &&
        (inventoryAffected || cannibalSelection.hasSelection)) {
      setPendingRepairAction(value);
      return;
    }
    applyRepairActionChange(value);
  };

  const handleBranchChange = (value) => {
    const preview = automaticReversalPreview
      ? buildAutomaticReversalPreview(initialSparePartPreview)
      : initialSparePartPreview;
    setHoldPreviewRows(preview.holdRows);
    setUsedPreviewRows(preview.usedRows);
    setSelectedHoldIds([]);
    setSelectedUsageIds([]);
    setPath("branch", value);
  };

  const toggleHoldSelection = (id) => {
    setSelectedHoldIds((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]));
  };

  const toggleUsageSelection = (id) => {
    setSelectedUsageIds((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]));
  };

  const addSelectedHolds = () => {
    const preview = moveSparePartPreviewRows(
      { holdRows: holdPreviewRows, usedRows: usedPreviewRows },
      "to_used",
      selectedHoldIds
    );
    setHoldPreviewRows(preview.holdRows);
    setUsedPreviewRows(preview.usedRows);
    setSelectedHoldIds([]);
  };

  const removeSelectedUsages = () => {
    const preview = moveSparePartPreviewRows(
      { holdRows: holdPreviewRows, usedRows: usedPreviewRows },
      "to_hold",
      selectedUsageIds
    );
    setHoldPreviewRows(preview.holdRows);
    setUsedPreviewRows(preview.usedRows);
    setSelectedUsageIds([]);
  };

  const validateForm = () => {
    const errors = { ...reviewValidationErrors };
    if (editDraft.repair_action === "part_replace" && cannibalSelection.enabled && cannibalCorrectionAllowed) {
      if (!cannibalSelection.donorId) errors.cannibal_device_id = "Select a Cannibal donor.";
      if (!cannibalSelection.itemIds?.length) errors.final_cannibal_item_ids = "Select at least one Item Master part.";
      if (cannibalSelection.error || !cannibalSelection.loaded) errors.cannibal_selection = cannibalSelection.error || "Cannibal options are still loading.";
    }
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      requestAnimationFrame(() => {
        const firstError = [...document.querySelectorAll('[data-field-error="true"]')]
          .find((node) => node.textContent.trim());
        const fieldGroup = firstError?.parentElement;
        const control = fieldGroup?.querySelector(
          'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])'
        );
        control?.focus();
        fieldGroup?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      });
    }
    return Object.keys(errors).length === 0;
  };

  const openReview = () => {
    if (!validateForm()) return;
    if (canEditServiceCost) {
      const costError = validateServiceCostDraft(editDraft.service_cost);
      if (costError) {
        setFormErrors((current) => ({ ...current, service_cost: costError }));
        return;
      }
    }
    setSaveError("");
    setReviewOpen(true);
  };

  const resetPreviewState = (loadedTicket, loadedSpareParts) => {
    const draft = buildEditDraft(loadedTicket);
    setEditDraft(draft);
    setFormErrors({});
    setBranchLocked(true);
    setCategoryLocked(true);
    setStageLocked(true);
    setSerialLocked(true);
    setPurchaseDateLocked(true);
    setDurationLocked(true);
    setSelectedHoldIds([]);
    setSelectedUsageIds([]);
    setSnCheckLoading(false);
    snCheckRequestIdRef.current += 1;

    const preview = buildSparePartPreview(loadedSpareParts);
    setHoldPreviewRows(preview.holdRows);
    setUsedPreviewRows(preview.usedRows);
  };

  const handleConfirmSave = async () => {
    if (saveLoading || saveRequestInFlightRef.current) return;
    const errors = computeStage3ValidationErrors({
      draft: editDraft,
      originalDraft,
      ticket,
      techniciansLoading,
      role: user?.role,
    });
    if (canEditServiceCost) {
      const costError = validateServiceCostDraft(editDraft.service_cost);
      if (costError) errors.service_cost = costError;
    }
    if (editDraft.repair_action === "part_replace" && cannibalSelection.enabled && cannibalCorrectionAllowed) {
      if (!cannibalSelection.donorId) errors.cannibal_device_id = "Select a Cannibal donor.";
      if (!cannibalSelection.itemIds?.length) errors.final_cannibal_item_ids = "Select at least one Item Master part.";
      if (cannibalSelection.error || !cannibalSelection.loaded) errors.cannibal_selection = cannibalSelection.error || "Cannibal options are still loading.";
    }
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const payload = pendingUpdatePayload;
    if (Object.keys(payload).length === 0) {
      setSaveError("No Service ticket changes were supplied.");
      return;
    }

    saveRequestInFlightRef.current = true;
    setSaveLoading(true);
    setSaveError("");
    try {
      await updateServiceTicket(ticketId, payload);
      let authoritativeTicket;
      let authoritativeSpareParts;
      try {
        const [ticketResponse, sparePartResponse] = await Promise.all([
          getServiceTicket(ticketId),
          getServiceSparePartOptions(ticketId),
        ]);
        authoritativeTicket = normalizeServiceTicketResponse(ticketResponse);
        authoritativeSpareParts = normalizeServiceSelectorResponse(sparePartResponse);
        setSparePartsLoaded(true);
        setSparePartsError("");
      } catch (refreshError) {
        setSparePartsLoaded(false);
        setSparePartsError(formatApiError(refreshError));
        throw new Error(`Service ticket was saved, but authoritative refresh failed: ${formatApiError(refreshError)}`);
      }

      setTicket(authoritativeTicket);
      setSpareParts(authoritativeSpareParts);
      resetPreviewState(authoritativeTicket, authoritativeSpareParts);
      setReviewOpen(false);
      setEditMode(false);
      setSaveError("");
      setNotice("Service ticket updated successfully.");
      navigate(viewRoute(), { replace: true });
    } catch (err) {
      setSaveError(formatApiError(err));
      setFormErrors((current) => ({ ...current, ...serviceFieldErrors(err) }));
    } finally {
      saveRequestInFlightRef.current = false;
      setSaveLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="pb-24" data-testid="service-detail-loading">
        <PersistentPageLoading
          label="Loading Service ticket..."
          contentClassName="px-8 sm:px-8 xl:px-8"
          bodyClassName="px-8 sm:px-8 xl:px-8"
          data-testid="service-detail-loading-header"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-[1504px] space-y-6 px-8 py-8" data-testid="service-detail-error">
        <Button
          type="button"
          onClick={backToList}
          variant="navigation-back"
          data-action-category="navigation-back"
        >
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
          Back
        </Button>
        <div className="rounded-sm border border-zinc-200 bg-white p-6 text-sm text-destructive dark:border-zinc-800 dark:bg-zinc-950">
          {error}
        </div>
      </div>
    );
  }

  const cannibalUsages = ticket.cannibal_usages || [];
  const linkedCannibalUsage = cannibalUsages.find((usage) => usage.usage_status === "active") || cannibalUsages[0];
  const cannibalDeviceSerialNumber = linkedCannibalUsage?.cannibal_device_serial_number || "";
  const cannibalDeviceModel = linkedCannibalUsage?.cannibal_device_model || "";
  const cannibalDeviceDisplay = [cannibalDeviceSerialNumber, cannibalDeviceModel].filter(Boolean).join(" - ");
  const hasCannibalDevice = Boolean(linkedCannibalUsage?.cannibal_device_id || cannibalDeviceSerialNumber);
  const viewCannibalLedger = {
    rows: (spareParts?.used_side || [])
      .filter((row) => row?.source_type === "cannibal" && row?.usage_status !== "reversed")
      .map((row) => ({ ...row, key: `view:${row.usage_id || row.id || row.item_id}` })),
  };
  const showCannibalStockSource = Boolean(
    ticket.category === "stock" && ticket.cannibal_source
  );
  const originalTechnician =
    editDraft.technician_id && !technicians.some((technician) => technician.id === editDraft.technician_id)
      ? [{ id: editDraft.technician_id, name: editDraft.technician_name || "Current technician", historical: true }]
      : [];
  const technicianOptions = [...originalTechnician, ...technicians];
  const deviceModelOptions = deviceModelOptionsWithHistory(
    deviceModels,
    originalDraft.device.model_selection,
    originalDraft.device.model
  );
  const baseChangedFields = buildChangedFields(originalDraft, effectiveDraft, technicianOptions, {
    role: user?.role,
    persistedStage: ticket?.stage,
  });
  const voidWarrantyChangedFields = buildVoidWarrantyReviewChanges({
    originalDraft,
    currentDraft: editDraft,
    managedVoidSuffix: ticket?.managed_void_suffix,
  });
  const ticketChangedFields = [...baseChangedFields, ...voidWarrantyChangedFields];
  const authoritativeDvtStatus = ticket?.warranty?.dvt?.status;
  const voidWarrantyControl = deriveVoidWarrantyControlState({
    authorized: editCapabilities.canEditVoidWarranty,
    busy: saveLoading,
    checked: editDraft.void_warranty,
    currentStatus: authoritativeDvtStatus,
    underlyingStatus: normalizedDvtPreview.status,
  });
  const effectivePartReplaceReview = deriveEffectivePartReplaceReviewState({
    initialPreview: initialSparePartPreview,
    draftPreview: { holdRows: holdPreviewRows, usedRows: usedPreviewRows },
    cannibalSelection,
    finalRepairAction: editDraft.repair_action,
  });
  const sparePartReview = buildSparePartReview(
    initialSparePartPreview,
    effectivePartReplaceReview.finalPreview,
    effectivePartReplaceReview.suppressDraftAdditions
  );
  const cannibalFinalDonorLabel = effectivePartReplaceReview.finalDonorLabel;
  const cannibalDonorChanged = effectivePartReplaceReview.donorChanged;
  const changedFields = cannibalDonorChanged
    ? [
        ...ticketChangedFields,
        {
          path: "cannibal_device_id",
          label: "Cannibal Device",
          before: cannibalSelection.savedDonorLabel || EMPTY,
          after: cannibalFinalDonorLabel,
        },
      ]
    : ticketChangedFields;
  const usedPartChanges = buildUnifiedUsedPartChanges(
    sparePartReview,
    effectivePartReplaceReview.cannibalSelection,
    effectivePartReplaceReview.suppressDraftAdditions
  );
  const reviewEnabled =
    editCapabilities.canEditTicket &&
    !snCheckLoading &&
    (!editCapabilities.canEditGeneralFields || !techniciansLoading) &&
    !saveLoading;
  const reviewConfirmDisabled =
    !hasUnsavedChanges ||
    Object.keys(reviewValidationErrors).length > 0 ||
    !reviewEnabled;
  const currentSerialNumber = editDraft.device.serial_number.trim();
  const originalSerialNumber = originalDraft.device.serial_number.trim();
  const serialNumberChanged = currentSerialNumber !== originalSerialNumber;
  const snResultMatchesCurrent = Boolean(
    editDraft.device.sn_check_result &&
      editDraft.device.sn_check_serial_number === currentSerialNumber
  );
  const unknownSnResult = ["unknown", "unavailable"].includes(editDraft.device.sn_check_result);
  const stockConfirmationEnabled = Boolean(
    isAdmin && serialNumberChanged && snResultMatchesCurrent && unknownSnResult
  );
  const snResultMessage = snResultMatchesCurrent
    ? editDraft.device.sn_check_result === "known"
      ? "SN found"
      : unknownSnResult
        ? "SN not found, please select a model and tick the stock reference"
        : ""
    : "";
  const snValidationMessage =
    formErrors["device.serial_number"] ||
    formErrors["device.sn_check"] ||
    formErrors["device.sn_check_api"] ||
    "";
  const serialMessage = snValidationMessage || snResultMessage;
  const serialMessageClass = snValidationMessage
    ? "text-destructive"
    : editDraft.device.sn_check_result === "known"
      ? "text-success"
      : "text-warning";
  const manualModelSelectionEnabled = Boolean(
    isAdmin && serialNumberChanged && snResultMatchesCurrent && unknownSnResult
  );
  const transferEnabled = canEditSparePartPreview({
    editMode,
    role: user?.role,
    repairAction: editDraft.repair_action,
    selectorLoaded: sparePartsLoaded,
    selectorError: sparePartsError,
    draftBranch: editDraft.branch,
    persistedBranch: ticket?.branch,
    saving: saveLoading,
  });
  const showSparePartSelection = shouldRenderSparePartSelection({
    editMode,
    persistedRepairAction: ticket?.repair_action,
    draftRepairAction: editDraft.repair_action,
  });
  const inventoryEditor = (cannibalLedger) => showSparePartSelection ? (
    <div className="order-4 mt-2 lg:col-span-3" data-testid="service-edit-spare-part-draft">
      <div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.08em] text-zinc-600 dark:text-zinc-300" data-testid="service-edit-spare-part-title">
        Sparepart Selection
      </div>
      {sparePartsLoading ? (
        <div className="rounded-sm border border-zinc-200 p-4 text-sm text-zinc-500 dark:border-zinc-800">Loading spare-part records...</div>
      ) : sparePartsError ? (
        <div className="rounded-sm border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">{sparePartsError}</div>
      ) : (
        <>
          {editMode && isAdmin && !branchCompatibleWithSelector ? (
            <p className="mb-3 rounded-sm border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning">Hold selection must be refreshed after the Branch change is saved. Existing Used rows remain visible.</p>
          ) : null}
          <div className="grid gap-4 xl:grid-cols-2">
            <SparePartPanel title="HOLD Part" rows={editMode ? holdPreviewRows : initialSparePartPreview.holdRows} editMode={editMode && editCapabilities.canEditSpareParts && !automaticReversalPreview} selectedIds={selectedHoldIds} onToggle={transferEnabled ? toggleHoldSelection : null} actionLabel="Add" onAction={addSelectedHolds} actionDisabled={!transferEnabled || selectedHoldIds.length === 0} />
            <UnifiedUsedPartPanel
              inventoryRows={editMode ? usedPreviewRows : initialSparePartPreview.usedRows}
              inventoryRowId={sparePartPreviewId}
              editMode={editMode && (editCapabilities.canEditSpareParts || cannibalCorrectionAllowed)}
              selectedInventoryIds={selectedUsageIds}
              onToggleInventory={transferEnabled ? toggleUsageSelection : null}
              onRemoveInventory={removeSelectedUsages}
              cannibalLedger={cannibalLedger}
            />
          </div>
        </>
      )}
    </div>
  ) : null;

  return (
    <div className="pb-24" data-testid="service-ticket-detail">
      <PersistentPageHeader
        context={<Badge tone="primary">{formatLabel(SERVICE_STAGE_LABELS, ticket.stage)}</Badge>}
        mode={editMode ? "EDIT" : "VIEW"}
        title={display(ticket.ticket_number)}
        metadata={`${formatLabel(SERVICE_BRANCH_LABELS, ticket.branch)} - ${display(ticket.owner?.name)}`}
        primaryActions={
          <PersistentHeaderUtilityAction
            type="button"
            ref={historyTriggerRef}
            onClick={openStageHistory}
            aria-label="View Stage History"
            data-action-category="history-audit"
            data-testid="service-detail-history"
          >
            <History className="h-3.5 w-3.5" strokeWidth={2} />
            View Stage History
          </PersistentHeaderUtilityAction>
        }
        secondaryActions={<>
          {editMode ? (
            <Button
              type="button"
              onClick={cancelEdit}
              variant="cancel"
              data-action-category="cancel"
              data-testid="service-detail-cancel"
            >
              Cancel
            </Button>
          ) : (
            <Button
              type="button"
              onClick={backToList}
              variant="navigation-back"
              data-action-category="navigation-back"
              data-testid="service-detail-back"
            >
              <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
              Back
            </Button>
          )}
            {!editMode && canRestoreTicket ? (
              <Button
                type="button"
                onClick={() => {
                  setRestoreError("");
                  setRestoreOpen(true);
                }}
                variant="restore"
                data-action-category="restore-recovery"
                data-testid="service-detail-restore"
              >
                <RotateCcw className="h-3.5 w-3.5" strokeWidth={2} />
                Restore
              </Button>
            ) : null}
            {!editMode && canEditTicket ? (
              <Button
                type="button"
                onClick={startEdit}
                variant="edit"
                data-action-category="edit"
                data-testid="service-detail-edit"
              >
                <Edit3 className="h-3.5 w-3.5" strokeWidth={2} />
                Edit
              </Button>
            ) : null}
            {editMode ? (
              <Button
                type="button"
                onClick={openReview}
                disabled={!reviewEnabled}
                title={reviewEnabled ? "Review changes" : "Make a valid change before reviewing."}
                variant="review"
                data-action-category="review-preview"
                data-testid="service-detail-review"
              >
                Review Changes
              </Button>
            ) : null}
        </>}
        contentClassName="px-8 sm:px-8 xl:px-8"
        contentTestId="service-detail-header-content"
        topRowTestId="service-detail-header-top-row"
        identityTestId="service-detail-header-identity"
        secondaryActionRowTestId="service-detail-header-bottom-row"
        data-testid="service-detail-header"
      />

      <div className="mx-auto mt-5 max-w-[1504px] space-y-5 px-8" data-testid="service-detail-content">
        {isDeleted ? (
        <div className="rounded-sm border border-destructive/30 bg-destructive/5 px-5 py-4" data-testid="service-deleted-banner">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-destructive">Deleted Service Ticket</div>
              <p className="mt-1 text-sm text-zinc-700 dark:text-zinc-200">
                This ticket is read-only and is excluded from all normal Stage lists.
              </p>
            </div>
            <Badge tone="primary">Last Stage: {formatLabel(SERVICE_STAGE_LABELS, ticket.stage)}</Badge>
          </div>
          <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-3">
            <div><dt className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">Deleted At</dt><dd className="mt-1 text-zinc-900 dark:text-zinc-100">{formatDateTime(ticket.deleted_at)}</dd></div>
            <div><dt className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">Deleted By</dt><dd className="mt-1 font-medium text-zinc-900 dark:text-zinc-100">{display(ticket.deleted_by)}</dd></div>
            <div><dt className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">Delete Reason</dt><dd className="mt-1 text-zinc-900 dark:text-zinc-100">{display(ticket.delete_reason)}</dd></div>
          </dl>
        </div>
      ) : null}

      {notice ? (
        <div className="rounded-sm border border-success/30 bg-success/10 px-4 py-3 text-sm font-semibold text-success">
          {notice}
        </div>
      ) : null}

      <div
        className="grid min-w-0 gap-5 xl:grid-cols-[minmax(0,7fr)_minmax(280px,3fr)]"
        data-testid="service-detail-layout"
      >
      <div
        className="contents xl:col-start-2 xl:row-start-1 xl:flex xl:min-w-0 xl:flex-col xl:gap-5"
        data-testid="service-detail-side-column"
      >
      <DetailSection number="1" title="Category (Kategori)" className="order-1 xl:order-none">
        <div className="space-y-4" data-testid="service-detail-category-layout">
          <div data-testid="service-detail-category-row-1" data-category-field="date_in">
            <SplitDateField label="Date In" value={ticket.date_in} readOnly yearOptions={OPERATIONAL_YEAR_OPTIONS} />
          </div>
          <div data-testid="service-detail-category-row-2" data-category-field="date_out">
            <SplitDateField label="Date Out" value={ticket.date_out} readOnly nullable yearOptions={OPERATIONAL_YEAR_OPTIONS} />
          </div>
          <div data-testid="service-detail-category-row-3" data-category-field="branch">
            {editMode && isAdmin ? (
              <LockedSegmentedControl
                label="Branch"
                value={editDraft.branch}
                options={BRANCH_OPTIONS}
                onChange={handleBranchChange}
                locked={branchLocked}
                onToggle={() => setBranchLocked((current) => !current)}
                error={formErrors.branch}
                testId="service-detail-branch-segments"
              />
            ) : (
              <SegmentedControl label="Branch" value={ticket.branch} options={BRANCH_OPTIONS} readOnly testId="service-detail-branch-segments" />
            )}
          </div>
          <div data-testid="service-detail-category-row-4" data-category-field="category">
            {editMode && isAdmin ? (
              <LockedSegmentedControl
                label="Category"
                value={editDraft.category}
                options={CATEGORY_OPTIONS}
                onChange={(value) => setPath("category", value)}
                locked={categoryLocked}
                onToggle={() => setCategoryLocked((current) => !current)}
                error={formErrors.category}
                testId="service-detail-category-segments"
              />
            ) : (
              <SegmentedControl label="Category" value={ticket.category} options={CATEGORY_OPTIONS} readOnly testId="service-detail-category-segments" />
            )}
          </div>
          <div data-testid="service-detail-category-row-5" data-category-field="stage">
            {editMode && isAdmin ? (
              <LockedSegmentedControl
                label="Stage"
                value={editDraft.stage}
                options={SERVICE_EDIT_STAGE_OPTIONS}
                onChange={(value) => setPath("stage", value)}
                locked={stageLocked}
                onToggle={() => setStageLocked((current) => !current)}
                error={formErrors.stage}
                testId="service-detail-stage-segments"
                columns={3}
              />
            ) : (
              <SegmentedControl label="Stage" value={ticket.stage} options={SERVICE_EDIT_STAGE_OPTIONS} readOnly testId="service-detail-stage-segments" columns={3} />
            )}
          </div>
        </div>
      </DetailSection>

      <DetailSection number="3" title="Owner" className="order-3 xl:order-none">
        <div className="grid gap-4 lg:grid-cols-3 xl:grid-cols-1">
          {editMode && editCapabilities.canEditGeneralFields ? (
            <>
              <TextField label="Company / Store Name" value={editDraft.owner.name} onChange={(value) => setPath("owner.name", value)} error={formErrors["owner.name"]} />
              <TextField label="PIC" value={editDraft.owner.pic} onChange={(value) => setPath("owner.pic", value)} />
              <TextField label="Contact" value={editDraft.owner.contact} onChange={(value) => setPath("owner.contact", value)} error={formErrors["owner.contact"]} />
              <TextField label="Address" value={editDraft.owner.address} onChange={(value) => setPath("owner.address", value)} multiline className="lg:col-span-3 xl:col-span-1" />
            </>
          ) : (
            <>
              <ReadField label="Company / Store Name" value={ticket.owner?.name} />
              <ReadField label="PIC" value={ticket.owner?.pic} />
              <ReadField label="Contact" value={ticket.owner?.contact} />
              <ReadField label="Address" value={ticket.owner?.address} className="lg:col-span-3 xl:col-span-1" multiline={!editMode} />
            </>
          )}
        </div>
      </DetailSection>

      <DetailSection number="4" title="Accessories" className="order-4 xl:order-none">
        {editMode && editCapabilities.canEditGeneralFields ? (
          <AccessoryEditor draft={editDraft} updateDraft={updateDraft} errors={formErrors} />
        ) : (
          <div className="space-y-4">
            <AccessoryReadOnly items={ticket.accessories?.items || []} />
            {ticket.accessories?.other_details ? (
              <ReadField label="Other Accessory Details" value={ticket.accessories.other_details} />
            ) : null}
          </div>
        )}
      </DetailSection>

      <DetailSection number="7" title="Logistics & Notes" className="order-7 xl:order-none">
        <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-1">
          {editMode && editCapabilities.canEditLogisticsFields ? (
            <>
              <TextField
                label="Expedition"
                value={editDraft.expedition}
                onChange={(value) => setPath("expedition", value)}
                error={formErrors.expedition}
              />
              <TextField
                label="Tracking Number"
                value={editDraft.tracking_number}
                onChange={(value) => setPath("tracking_number", value)}
                error={formErrors.tracking_number}
              />
              <TextField label="Notes" value={editDraft.notes} onChange={(value) => setPath("notes", value)} multiline className="lg:col-span-2 xl:col-span-1" />
            </>
          ) : (
            <>
              <ReadField label="Expedition" value={ticket.expedition} />
              <ReadField label="Tracking Number" value={ticket.tracking_number} />
              <ReadField label="Notes" value={ticket.notes} className="lg:col-span-2 xl:col-span-1" multiline={!editMode} />
            </>
          )}
        </div>
      </DetailSection>
      </div>

      <div
        className="contents xl:col-start-1 xl:row-start-1 xl:flex xl:min-w-0 xl:flex-col xl:gap-5"
        data-testid="service-detail-main-column"
      >
      <DetailSection number="2" title="Device" className="order-2 xl:order-none">
        {editMode && editCapabilities.canEditGeneralFields ? (
          <div className="space-y-5" data-testid="service-detail-device-layout">
            <div className="grid gap-5 lg:grid-cols-2" data-testid="service-detail-device-row-1" data-device-fields="serial_number,generate|model_selection,model">
              <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start" data-testid="service-detail-device-row-1-left">
                <div>
                  <div className="flex h-4 items-center">
                    <label className="text-[10px] font-semibold uppercase tracking-[0.08em] text-zinc-600 dark:text-zinc-300">
                      Serial Number
                    </label>
                  </div>
                  <div className="mt-2">
                    <CompoundInputControl
                      value={isAdmin ? editDraft.device.serial_number : ticket.device?.serial_number || ""}
                      onChange={isAdmin ? (value) => setPath("device.serial_number", value) : undefined}
                      readOnly={!isAdmin || serialLocked}
                      endAdornment={isAdmin ? (
                        <LockButton
                          locked={serialLocked}
                          onClick={() => setSerialLocked((current) => !current)}
                          disabled={!editCapabilities.canUnlockAdminFields}
                          label={serialLocked ? "Unlock Serial Number" : "Lock Serial Number"}
                        />
                      ) : null}
                    />
                  </div>
                  <p
                    className={`mt-1 min-h-[18px] text-xs ${serialMessageClass}`}
                    data-testid="service-detail-serial-message"
                  >
                    {isAdmin ? serialMessage : ""}
                  </p>
                </div>
                {isAdmin ? (
                  <div>
                    <div className="h-4" aria-hidden="true" />
                    <Button
                      type="button"
                      onClick={handleGenerateSnCheck}
                      disabled={serialLocked}
                      loading={snCheckLoading}
                      loadingLabel="Generating..."
                      variant="info"
                      className="mt-2"
                      data-action-category="utility"
                      data-testid="service-detail-generate"
                    >
                      Generate
                    </Button>
                  </div>
                ) : <div className="hidden lg:block" aria-hidden="true" />}
              </div>
              <div className="grid gap-3 sm:grid-cols-2" data-testid="service-detail-device-row-1-right">
                <SelectField
                  label="Model Selection"
                  value={editDraft.device.model_selection}
                  onChange={(value) => setPath("device.model_selection", value)}
                  disabled={!manualModelSelectionEnabled || deviceModelsLoading}
                  error={formErrors["device.model_selection"] || deviceModelsError}
                >
                  <option value="">{deviceModelsLoading ? "Loading models..." : "Select model"}</option>
                  {deviceModelOptions.map((option) => (
                    <option key={option.value} value={option.value} disabled={option.historical}>
                      {option.label}
                    </option>
                  ))}
                </SelectField>
                <TextField label="Model" value={editDraft.device.model} readOnly />
              </div>
            </div>

            <div className="grid gap-5 lg:grid-cols-2" data-testid="service-detail-device-row-2" data-device-fields="purchase_date|stock_reference_status,dvt_duration_years">
              <SplitDateField
                label="Purchase Date"
                value={editDraft.device.purchase_date}
                onChange={(value) => setPath("device.purchase_date", value)}
                readOnly={purchaseDateLocked}
                error={formErrors["device.purchase_date"]}
                helper={editDraft.device.purchase_date_sn_message}
                yearOptions={OPERATIONAL_YEAR_OPTIONS}
                endAdornment={
                  <LockButton
                    locked={purchaseDateLocked}
                    onClick={() => setPurchaseDateLocked((current) => !current)}
                    disabled={!editCapabilities.canUnlockSharedFields}
                    label={purchaseDateLocked ? "Unlock Purchase Date" : "Lock Purchase Date"}
                  />
                }
              />
              <div className="grid gap-3 sm:grid-cols-2" data-testid="service-detail-device-row-2-right">
                <div>
                  <div className="flex h-4 items-center">
                    <label id="service-detail-stock-reference-label" className="text-[10px] font-semibold uppercase tracking-[0.08em] text-zinc-600 dark:text-zinc-300">
                      Stock Reference Status
                    </label>
                  </div>
                  <div
                    id="service-detail-stock-reference"
                    role="group"
                    aria-labelledby="service-detail-stock-reference-label"
                    data-field-state="readonly"
                    data-testid="service-detail-stock-reference-field"
                    className="field-readonly mt-2 flex h-11 w-full items-center justify-between gap-3 rounded-sm border px-3 text-sm outline-none"
                  >
                    <span>{editDraft.device.stock_reference_status || "Not generated"}</span>
                    {editDraft.device.stock_reference_status === "unknown" ? (
                    <input
                      type="checkbox"
                      checked={editDraft.unknown_stock_reference_confirmed}
                      onChange={(event) => setPath("unknown_stock_reference_confirmed", event.target.checked)}
                      disabled={!stockConfirmationEnabled}
                      required={stockConfirmationEnabled}
                      aria-label="Confirm unknown stock reference"
                      className="h-4 w-4 shrink-0 accent-primary disabled:cursor-not-allowed disabled:opacity-50"
                      data-testid="service-detail-stock-confirmation"
                    />
                    ) : null}
                  </div>
                  <FieldError error={formErrors.unknown_stock_reference_confirmed} />
                </div>
                <TextField
                  label="DVT Warranty Duration"
                  value={editDraft.warranty.dvt.duration_years}
                  onChange={(value) => setPath("warranty.dvt.duration_years", value)}
                  readOnly={durationLocked}
                  type="number"
                  error={formErrors["warranty.dvt.duration_years"]}
                  endAdornment={
                    <LockButton
                      locked={durationLocked}
                      onClick={() => setDurationLocked((current) => !current)}
                      disabled={!editCapabilities.canUnlockSharedFields}
                      label={durationLocked ? "Unlock DVT Warranty Duration" : "Lock DVT Warranty Duration"}
                    />
                  }
                />
              </div>
            </div>

            <div className="grid gap-5 lg:grid-cols-2" data-testid="service-detail-device-row-3" data-device-fields="dvt_expiry_date,dvt_status">
              <SplitDateField label="DVT Warranty Exp Date" value={effectiveDraft.warranty.dvt.expiry_date} readOnly yearOptions={EXPIRY_YEAR_OPTIONS} />
              <ReadOnlyDvtBadgeField
                status={effectiveDraft.warranty.dvt.status}
                error={formErrors.void_reason || formErrors.void_warranty}
                labelAdornment={(
                  <VoidWarrantySwitch
                    id="service-edit-void-warranty"
                    checked={editDraft.void_warranty}
                    disabled={voidWarrantyControl.disabled}
                    onChange={updateVoidWarranty}
                  />
                )}
              />
            </div>

            <div className="grid gap-5 lg:grid-cols-2" data-testid="service-detail-device-row-4" data-device-fields="imin_expiry_date,imin_status">
              <SplitDateField
                label="iMin Warranty Exp Date"
                value={editDraft.warranty.imin.expiry_date}
                onChange={(value) => setPath("warranty.imin.expiry_date", value)}
                error={formErrors["warranty.imin.expiry_date"]}
                helper={editDraft.warranty.imin.sn_check_message}
                helperTestId="service-detail-imin-warranty-message"
                helperTone={editDraft.warranty.imin.lookup_result === "found" ? "success" : "warning"}
                nullable
                yearOptions={EXPIRY_YEAR_OPTIONS}
              />
              <SelectField label="iMin Warranty Status" value={editDraft.warranty.imin.status} onChange={(value) => setPath("warranty.imin.status", value)}>
                {IMIN_STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </SelectField>
            </div>
          </div>
        ) : (
          <div className="space-y-5" data-testid="service-detail-device-layout">
            <div className="grid gap-5 lg:grid-cols-2" data-testid="service-detail-device-row-1" data-device-fields="serial_number|model_selection,model">
              <TextField label="Serial Number" value={ticket.device?.serial_number} readOnly showAbsent />
              <div className="grid gap-3 sm:grid-cols-2" data-testid="service-detail-device-row-1-right">
                <TextField label="Model Selection" value={ticket.device?.model_selection} readOnly showAbsent />
                <TextField label="Model" value={ticket.device?.model} readOnly showAbsent />
              </div>
            </div>
            <div className="grid gap-5 lg:grid-cols-2" data-testid="service-detail-device-row-2" data-device-fields="purchase_date|stock_reference_status,dvt_duration_years">
              <SplitDateField label="Purchase Date" value={ticket.device?.purchase_date} readOnly yearOptions={OPERATIONAL_YEAR_OPTIONS} />
              <div className="grid gap-3 sm:grid-cols-2" data-testid="service-detail-device-row-2-right">
                <TextField label="Stock Reference Status" value={ticket.stock_reference_status || ticket.device?.stock_reference_status} readOnly showAbsent />
                <TextField label="DVT Warranty Duration" value={ticket.warranty?.dvt?.duration_years} readOnly showAbsent />
              </div>
            </div>
            <div className="grid gap-5 lg:grid-cols-2" data-testid="service-detail-device-row-3" data-device-fields="dvt_expiry_date,dvt_status">
              <SplitDateField label="DVT Warranty Exp Date" value={ticket.warranty?.dvt?.expiry_date} readOnly yearOptions={EXPIRY_YEAR_OPTIONS} />
              <ReadOnlyDvtBadgeField
                status={ticket.warranty?.dvt?.status}
                labelAdornment={editMode ? (
                  <VoidWarrantySwitch
                    id="service-edit-void-warranty"
                    checked={ticket.warranty?.dvt?.status === "void"}
                    disabled
                  />
                ) : null}
              />
            </div>
            <div className="grid gap-5 lg:grid-cols-2" data-testid="service-detail-device-row-4" data-device-fields="imin_expiry_date,imin_status">
              <SplitDateField label="iMin Warranty Exp Date" value={ticket.warranty?.imin?.expiry_date} readOnly nullable yearOptions={EXPIRY_YEAR_OPTIONS} />
              <SelectField label="iMin Warranty Status" value={ticket.warranty?.imin?.status || "unknown"} disabled>
                {IMIN_STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </SelectField>
            </div>
          </div>
        )}
      </DetailSection>

      <DetailSection number="5" title="Problem & Resolution" className="order-5 xl:order-none">
        {showCannibalStockSource ? (
          <div className="mb-5">
            <CannibalStockSourceSnapshot
              cannibalId={ticket.cannibal_source.cannibal_id}
              removedParts={ticket.cannibal_source.removed_parts}
              testId="service-cannibal-stock-source"
            />
          </div>
        ) : null}
        <div className="grid gap-4 lg:grid-cols-2">
          {editMode && editCapabilities.canEditGeneralFields ? (
            <>
              <TextField label="Problem" value={editDraft.problem} onChange={(value) => setPath("problem", value)} multiline error={formErrors.problem} />
              <TextField label="Root Cause" value={editDraft.root_cause} onChange={(value) => setPath("root_cause", value)} multiline error={formErrors.root_cause} />
              <div className="lg:col-span-2">
                <label
                  className="text-[10px] font-semibold uppercase tracking-[0.08em] text-zinc-600 dark:text-zinc-300"
                  htmlFor="service-edit-resolution"
                >
                  Resolution
                </label>
                <ProtectedResolutionField
                  id="service-edit-resolution"
                  value={editDraft.resolution}
                  onChange={(value) => setPath("resolution", value)}
                  persistedVoid={ticket?.warranty?.dvt?.status === "void"}
                  showLockedSuffix={editDraft.void_warranty}
                  draftReason={editDraft.void_warranty_touched ? editDraft.void_reason : ""}
                  disabled={saveLoading}
                  textareaClassName="mt-2 min-h-[96px] w-full resize-none rounded-sm border border-zinc-200 px-3 py-2.5 text-sm text-zinc-900 outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/10 disabled:cursor-not-allowed disabled:opacity-45 dark:border-zinc-700 dark:text-zinc-100"
                  containerClassName="h-[96px]"
                />
                <FieldError error={formErrors.resolution} />
              </div>
              <div className="lg:col-span-2"><TextField label="Pending Reason" value={editDraft.pending_reason} onChange={(value) => setPath("pending_reason", value)} multiline error={formErrors.pending_reason} /></div>
              <div className="grid gap-4 lg:col-span-2 lg:grid-cols-3">
                {editCapabilities.canEditRepairAction || user?.role === "staff" ? (
                  <div className="order-1"><SelectField label="Repair Action" value={editDraft.repair_action} onChange={handleRepairActionChange} error={formErrors.repair_action}>
                    <option value="">Select action</option>
                    {REPAIR_ACTION_OPTIONS.filter((option) => editCapabilities.canEditRepairAction || option.value === ticket.repair_action).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </SelectField></div>
                ) : (
                  <div className="order-1"><ReadField label="Repair Action" value={formatLabel(REPAIR_ACTION_LABELS, ticket.repair_action)} /></div>
                )}
                <CannibalPartSelector ticket={ticket} active={editDraft.repair_action === "part_replace"} disabled={!editMode || !cannibalCorrectionAllowed || saveLoading} onStateChange={setCannibalSelection} fieldClassName="order-2" triggerClassName="h-11" inventoryContent={inventoryEditor} />
                <div className="order-3">
                  <SelectField label="Technician" value={editDraft.technician_id} onChange={(value) => {
                    const technician = technicianOptions.find((item) => item.id === value);
                    setPath("technician_id", value);
                    setPath("technician_name", technician?.name || "");
                  }} error={formErrors.technician_id} disabled={techniciansLoading}>
                    <option value="">{techniciansLoading ? "Loading technicians..." : "Select technician"}</option>
                    {technicianOptions.map((technician) => <option key={technician.id} value={technician.id} disabled={technician.historical === true}>{technician.name}</option>)}
                  </SelectField>
                  {techniciansError ? <p className="text-xs text-destructive">{techniciansError}</p> : null}
                </div>
              </div>
            </>
          ) : (
            <>
              <ReadField label="Problem" value={ticket.problem} multiline={!editMode} />
              <ReadField label="Root Cause" value={ticket.root_cause} multiline={!editMode} />
              <div className="lg:col-span-2"><ReadField label="Resolution" value={ticket.resolution} multiline={!editMode} /></div>
              <div className="lg:col-span-2"><ReadField label="Pending Reason" value={ticket.pending_reason} multiline={!editMode} /></div>
              {editMode ? (
                <div className="grid gap-4 lg:col-span-2 lg:grid-cols-3">
                  <div className="order-1"><ReadField label="Repair Action" value={formatLabel(REPAIR_ACTION_LABELS, ticket.repair_action)} /></div>
                  <CannibalPartSelector ticket={ticket} active={editDraft.repair_action === "part_replace"} disabled={!cannibalCorrectionAllowed || saveLoading} onStateChange={setCannibalSelection} fieldClassName="order-2" triggerClassName="h-10" inventoryContent={inventoryEditor} />
                  <div className="order-3"><ReadField label="Technician" value={ticket.technician_name} /></div>
                </div>
              ) : (
                <div className="grid gap-4 lg:col-span-2 md:grid-cols-3">
                  <ReadField label="Repair Action" value={formatLabel(REPAIR_ACTION_LABELS, ticket.repair_action)} />
                  <div data-testid="service-view-cannibal-device">
                    <label className="flex min-h-[18px] items-center gap-2 text-xs font-semibold text-zinc-500">
                      <input type="checkbox" checked={hasCannibalDevice} disabled readOnly className="h-4 w-4 rounded-sm accent-primary" />
                      Cannibal Device
                    </label>
                    <div className="mt-2 min-h-[42px] rounded-sm border border-zinc-200 bg-zinc-100 px-3 py-2.5 text-sm text-zinc-600 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300">{display(cannibalDeviceDisplay)}</div>
                  </div>
                  <ReadField label="Technician" value={ticket.technician_name} />
                </div>
              )}
            </>
          )}
        </div>

        {!editMode && showSparePartSelection ? (
        <div className="mt-6" data-testid="service-view-spare-part-selection">
          <div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.08em] text-zinc-600 dark:text-zinc-300" data-testid="service-view-spare-part-title">
            Sparepart Selection
          </div>
          {formErrors.cannibal_device_id || formErrors.final_cannibal_item_ids || formErrors.cannibal_selection ? <p className="mt-2 text-xs text-destructive">{formErrors.cannibal_device_id || formErrors.final_cannibal_item_ids || formErrors.cannibal_selection}</p> : null}
          {!editMode && sparePartsLoading ? (
            <div className="rounded-sm border border-zinc-200 p-4 text-sm text-zinc-500 dark:border-zinc-800">
              Loading spare-part records...
            </div>
          ) : null}
          {!editMode && sparePartsError ? (
            <div className="rounded-sm border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
              {sparePartsError}
            </div>
          ) : null}
          {!editMode && !sparePartsLoading && !sparePartsError ? (
            <div className="grid gap-4 xl:grid-cols-2">
              <SparePartPanel title="HOLD Part" rows={initialSparePartPreview.holdRows} editMode={false} />
              <UnifiedUsedPartPanel
                inventoryRows={initialSparePartPreview.usedRows}
                inventoryRowId={sparePartPreviewId}
                editMode={false}
                cannibalLedger={viewCannibalLedger}
              />
            </div>
          ) : null}
        </div>
        ) : null}
      </DetailSection>

      <DetailSection number="6" title="Service Cost" className="order-6 xl:order-none">
        <ServiceCostSection
          serviceTicketScope3
          value={ticket.service_cost}
          draft={editDraft.service_cost}
          preview={serviceCostPreview}
          editable={editMode && canEditServiceCost}
          readableViewValues={!editMode}
          onChange={(serviceCost) => {
            setEditDraft((current) => ({ ...current, service_cost: serviceCost }));
            setFormErrors((current) => {
              const next = { ...current };
              delete next.service_cost;
              return next;
            });
          }}
          error={formErrors.service_cost}
        />
      </DetailSection>
      </div>
      </div>
      </div>

      <ServiceStageHistoryDialog
        open={historyOpen}
        loading={historyLoading}
        error={historyError}
        records={historyRecords}
        ticket={ticket}
        onClose={closeStageHistory}
      />
      <ReviewChangesModal
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        onConfirm={handleConfirmSave}
        changedFields={changedFields}
        usedPartChanges={usedPartChanges}
        automaticReversal={sparePartReview.automaticReversal}
        saving={saveLoading}
        error={saveError}
        confirmDisabled={reviewConfirmDisabled}
        serviceCostMutation={{
          custom_cost_commands: pendingUpdatePayload.custom_cost_commands,
          discount: pendingUpdatePayload.discount || systemDiscountReview,
        }}
        serviceCostPreview={canEditServiceCost ? serviceCostPreview : ticket.service_cost}
        originalServiceCost={ticket.service_cost}
        serviceCostDraft={effectiveServiceCostDraft}
      />
      <ServiceLifecycleDialog
        mode="restore"
        open={restoreOpen}
        ticket={ticket}
        error={restoreError}
        onClose={closeRestore}
        onConfirm={confirmRestore}
      />
      <UnsavedChangesDialog
        open={pendingDiscardAction !== null}
        onStay={() => setPendingDiscardAction(null)}
        onLeave={() => {
          const action = pendingDiscardAction;
          setPendingDiscardAction(null);
          action?.();
        }}
      />
      <AlertDialog open={pendingRepairAction !== null} onOpenChange={(nextOpen) => !nextOpen && setPendingRepairAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change Repair Action?</AlertDialogTitle>
            <AlertDialogDescription>The spare-part draft will be hidden and preserved if you switch back before saving. A final non-Part-Replace save reverses active Inventory and Cannibal Usage.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => applyRepairActionChange(pendingRepairAction)}>Continue</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
